import { mountContentReview } from './content-review.js';
import { mountTeacherOps } from './teacher-ops.js';
import { mountReviewTasks } from './review-tasks.js';
import { mountStudyGoal } from './study-goal.js';
import { mountLearningRecord } from './learning-record.js';
import { mountLearningSetup, mountHomeMaterials } from './learning-setup.js';
import { toggleButton } from './toggle-button.js';
import { mountReview } from './review-panel.js';
import { mountResultDetail } from './result-detail.js';
import { ResponseClock } from '../core/response-clock.js';
/**
 * 학생 화면 흐름: 로그인 → 본인 상태 → 과제 목록·시작 전 정보 → 풀이·저장 → 이어 풀기 → 제출.
 * 상태·문구는 디자인 v0.3 state-table.md(+ v0.4.1 보완 규칙)를 따른다. 화면은 계정 상태·소속·소유자를 판단하지 않고 서버 응답만 표시한다.
 *
 * 요청 문맥(v0.2, 총괄 DEV-I-002 항목 1): 응답을 기다린 뒤에는 항상 요청을 보낼 때의 세대·계정·시도가 지금과 같은지 확인한다.
 * 다르면 그 응답은 상태·화면 구성·오류 안내에 쓰지 않고 버린다(live()).
 */
import { devlog, exposeDevlog } from '../core/devlog.js';
import { unansweredGuide } from '../core/guide.js';
import { lockoutMessage } from '../core/lockout.js';
import { precheck } from '../core/precheck.js';
import { choiceFromSaved, reconcileTarget, SaveManager, type PrevResult, type SaveEvent, type ServerValue } from '../core/save-manager.js';
import { SubmitController, type SubmitEvent, type SubmitTiming } from '../core/submit-controller.js';
import { bumpGen, currentGen, fetchTransport, newRequestKey, type ApiResult, type Transport } from '../core/transport.js';
import { grValueFromSaved, sameValue, type AssignmentInfo, type ItemsResponse, type Me, type TargetValue } from '../core/types.js';
import { readQuizCheckMs, readResultCheckMs, readTiming, readWordsCheckMs } from './config.js';
import { answerBody, applyAccepted, decideAnswer, decideQuizGet, decideQuizList, decideQuizResult, decideStart, nextUnanswered, quizResponseUsable, type PendingAnswer, type QuizListRow, type QuizReqCtx } from '../core/quiz-model.js';
import { blankQuiz, renderQuizList, renderQuizPlay, renderQuizResult, type QuizHandlers, type QuizState } from './quiz.js';
import { decideWords, wordsResponseUsable, type WordItem, type WordsReqCtx } from '../core/words-model.js';
import { blankWordDetail, blankWordList, renderWordDetail, renderWordList, type WordsState } from './words.js';
import { awaitResultBatch } from '../core/result-batch.js';
import { EvidenceSubmitter, type PendingSubmit } from '../core/evidence-submit.js';
import { decideEvidenceBatch, type EvidenceQuestion } from '../core/evidence-model.js';
import { blankEvidence, blankTab, renderEvidence, type EvidenceState, type EvidenceTab } from './evidence.js';
import { clear, h, toast } from './dom.js';
import { hideShell, mountShell } from './shell.js';
import { formatKst, SolveView } from './solve.js';
import { changedPoints, decideBatch, decideEarly, joinPoints } from '../core/result-model.js';
import { blankResult, renderResult, type ResultState } from './result.js';

const $app = () => document.getElementById('app')!;
const $ov = () => document.getElementById('ov-root')!;
const ASSIGNMENT_PATH = /^\/a\/([0-9a-fA-F-]{36})\/?$/;

function card(...children: (Node | string | null | false)[]): HTMLElement {
  return h('div', { class: 'full' }, h('div', { class: 'card' },
    h('p', { class: 'brand-text', 'data-testid': 'brand' }, '그로잉어학원'),
    ...children));
}
function sheet(testid: string, ...children: (Node | string | null | false)[]): HTMLElement {
  return h('div', { class: 'ov', 'data-testid': `ov-${testid}` }, h('div', { class: 'sheet', role: 'dialog', 'aria-modal': 'true' }, ...children));
}
const CHANNEL_LABEL: Record<string, string> = { screen: '화면에서 고르기', omr: '종이 OMR 번호 입력' };
export const channelText = (cs: readonly string[]) => cs.map((c) => CHANNEL_LABEL[c] ?? c).join(' · ');
const STATUS_LABEL: Record<string, string> = { 'not-started': '시작 전', 'in-progress': '푸는 중', submitted: '제출 완료', void: '사용할 수 없는 풀이' };

/** 요청을 보낼 때의 문맥 */
interface Ctx { readonly gen: number; readonly me: string | null; readonly attempt: string | null }

export class App {
  private readonly t: Transport;
  private me: Me | null = null;
  private owner: string | null = null;
  private assignmentId: string | null = null;
  /** 학생 첫 화면의 구역: 홈(요약·소수 항목) / 내 학습 / 오답·근거 복습 / 학습 기록. 화면 안 상태일 뿐 학습 데이터가 아니다 */
  private homeTab: 'home' | 'learn' | 'review' | 'record' = 'home';
  private goTab(tab: 'home' | 'learn' | 'review' | 'record'): void { this.homeTab = tab; this.onExit(null); }
  private attemptId: string | null = null;
  private data: ItemsResponse | null = null;
  private sm: SaveManager | null = null;
  private sc: SubmitController | null = null;
  private view: SolveView | null = null;
  private readonly deviceId = 'web-' + newRequestKey().slice(-10);
  private conflictQueue: string[] = [];
  private conflictOpen = false;
  private reloginOpen = false;
  private submitWait: { cancel: () => void } | null = null;
  private readonly timing: SubmitTiming;
  /** GR 결과 화면(DEV-I-007). 결과 화면을 열 때마다 새로 만든다(이전 시도·계정 값 재사용 없음) */
  private result: ResultState | null = null;
  /** 결과 조회 요청 번호(전역 증가, 화면을 다시 열어도 이어짐) */
  private resultSeq = 0;
  /** 진행 중인 결과 화면 조회 묶음의 취소 손잡이(GET만). 묶음이 끝나거나 화면을 떠나면 abort */
  private resultAbort: AbortController | null = null;
  private readonly resultCheckMs = readResultCheckMs();
  /** 근거 문제 화면(DEV-I-010). 열 때마다 새로 만든다 */
  private evidence: EvidenceState | null = null;
  private evAbort: AbortController | null = null;
  /** 이 탭에만 있는 근거 문제 값(고른 보기·거부 안내·확정 표시). 계정이 바뀌거나 로그아웃하면 지운다. 화면을 떠나도 남는다 */
  private readonly evTab: EvidenceTab = blankTab();
  private readonly evLastQ = new Map<string, EvidenceQuestion>();
  /** 근거 응답 제출(배정 단위, 미확정 기록·같은 키 재전송). 화면을 떠나도 남는다 */
  private readonly submitter: EvidenceSubmitter;
  /** 내 단어장 목록·상세(DEV-I-013). 화면을 열 때마다 새로 만든다(이전 값 재사용 없음) */
  private words: WordsState | null = null;
  private wordsAbort: AbortController | null = null;
  private wordsSeq = 0;
  private readonly wordsCheckMs = readWordsCheckMs();
  /** 단어 시험 화면(DEV-I-020). 화면을 열 때마다 새로 만든다 */
  private quiz: QuizState | null = null;
  private readonly responseClock = new ResponseClock(() => performance.now());
  private quizAbort: AbortController | null = null;
  private quizSeq = 0;
  private readonly quizCheckMs = readQuizCheckMs();
  /** 이 탭에만 있는 시험 제출 값: 시작 요청 키(확정 전)와 문항 응답 미확정 기록(같은 키·같은 내용). 계정이 바뀌거나 로그아웃하면 지운다 */
  private readonly quizTab: { owner: string | null; startKey: string | null; pending: PendingAnswer | null } = { owner: null, startKey: null, pending: null };

  constructor(base?: Transport) {
    const inner = base ?? fetchTransport();
    // 연결 상태 띠: 응답을 하나라도 받으면 연결됨, 응답이 없으면 끊김
    this.t = {
      request: async (m, p, o) => {
        const r = await inner.request(m, p, o);
        if (r.gen === currentGen()) this.view?.setOffline(r.kind === 'network');
        return r;
      },
    };
    this.assignmentId = ASSIGNMENT_PATH.exec(location.pathname)?.[1] ?? null;
    this.timing = readTiming();
    const onThisScreen = (p: PendingSubmit) => this.evidence !== null && this.evidence.attemptId === p.attemptId && this.me?.id === p.owner;
    this.submitter = new EvidenceSubmitter({
      transport: this.t, newKey: newRequestKey, timing: this.timing, now: () => Date.now(),
      setTimeout: (fn, ms) => window.setTimeout(fn, ms), clearTimeout: (id) => window.clearTimeout(id as number),
      me: () => this.me?.id ?? null,
      onChange: () => { if (this.evidence !== null) this.renderEvidenceScreen(); },
      onConfirmed: (p, kind) => {
        this.evTab.drafts.delete(p.assignmentId); this.evTab.notes.delete(p.assignmentId);
        this.evTab.confirmed.set(p.assignmentId, { attemptId: p.attemptId, already: kind === 'already' });
        this.evTab.awaiting.add(p.assignmentId);
        if (onThisScreen(p)) { this.renderEvidenceScreen(); void this.loadEvidence('after-submit'); }
      },
      onRejected: (p, note) => {
        // 제출 안 됨: 상태·입력 거부면 고른 보기를 지운다(다시 골라 새 키로). 문제 재제공이면 고른 보기는 남긴다
        // 거부 확정 뒤에는 모두 선택을 지운다(DEV-I-011: question_not_provided도). 다시 고르고 '제출하기'를 누를 때 새 키
        this.evTab.drafts.delete(p.assignmentId);
        if (note === 'reprovide') { this.evTab.notes.delete(p.assignmentId); this.evTab.reload.add(p.assignmentId); } // 안내는 재조회가 성공해야
        else this.evTab.notes.set(p.assignmentId, { kind: note, fresh: true });
        if (onThisScreen(p)) { this.renderEvidenceScreen(); void this.loadEvidence('after-reject'); }
      },
      onCheck: (p) => { if (onThisScreen(p) && !this.evidence!.busy) void this.loadEvidence('check'); },
      onRelogin: (p) => { if (onThisScreen(p)) this.openRelogin(); },
      onAccess: (p, r) => {
        if (!onThisScreen(p)) return;
        const S = this.evidence!;
        const d = r.status === 404 ? 'notfound' : r.body['error'] === 'content_not_released' ? 'denied' : 'account';
        S.ev = null; S.phase = d; this.renderEvidenceScreen();
        if (d === 'account') void this.refreshStatusScreen();
      },
      onDevlog: (code, detail) => devlog('evidence', code, null, detail),
    });
  }

  /** 근거 문제 탭 값의 계정. 다른 계정(또는 로그아웃)이면 고른 보기·미확정 기록·확정 표시를 모두 지운다 */
  private tabFor(meId: string | null): void {
    if (this.quizTab.owner !== meId) { this.responseClock.clear(); this.quizTab.owner = meId; this.quizTab.startKey = null; this.quizTab.pending = null; }
    if (this.evTab.owner === meId) return;
    this.evTab.owner = meId;
    this.evTab.drafts.clear(); this.evTab.notes.clear(); this.evTab.confirmed.clear(); this.evTab.awaiting.clear();
    this.evTab.reload.clear(); this.evTab.conflictAsked.clear(); this.evTab.conflictSeen.clear();
    this.evLastQ.clear();
    this.submitter.clear();
  }

  // ---------------------------------------------------------------- 요청 문맥

  private ctx(): Ctx { return { gen: currentGen(), me: this.me?.id ?? null, attempt: this.attemptId }; }
  /** 응답을 기다린 뒤: 요청 당시의 세대·계정·시도가 지금과 같은가 */
  private live(c: Ctx): boolean { return c.gen === currentGen() && c.me === (this.me?.id ?? null) && c.attempt === this.attemptId; }

  async boot(): Promise<void> {
    exposeDevlog();
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.responseClock.pause(); else this.syncResponseClock();
    });
    // 배치는 CSS가 학생 화면의 실제 폭(컨테이너 쿼리)으로 정한다. 폭이 바뀌어도(회전·화면 분할) 화면을 다시 그리지 않으므로
    // 선택·미저장 입력·OMR 값·대화 상자·제출 잠금이 그대로 남는다. 여기서는 입력란을 보이는 곳에 두는 처리만 한다.
    keepInputVisibleOnResize();
    window.addEventListener('beforeunload', (e) => {
      // L-2: 브라우저가 확인 창을 띄우지 않을 수 있다. 문구도 브라우저가 정한다.
      if (this.hasUnsaved() || (this.sc?.uncertain() ?? false)) { e.preventDefault(); e.returnValue = ''; }
    });
    window.addEventListener('popstate', () => {
      if (this.sm !== null || this.me === null) return; // 풀이 중에는 나가기 확인을 거친다
      this.assignmentId = ASSIGNMENT_PATH.exec(location.pathname)?.[1] ?? null;
      this.showHome();
    });
    const c = this.ctx();
    const r = await this.t.request('GET', '/me');
    if (!this.live(c)) return;
    if (r.kind === 'http' && r.status === 200) this.afterMe(r.body as Me);
    else this.showLogin(r.kind === 'network' ? '연결을 확인한 뒤 다시 시도해 주세요.' : null);
  }

  private navigate(assignmentId: string | null): void {
    this.assignmentId = assignmentId;
    const path = assignmentId === null ? '/' : `/a/${assignmentId}`;
    if (location.pathname !== path) history.pushState(null, '', path);
    this.showHome();
  }

  // ---------------------------------------------------------------- 로그인·계정 상태 (2장)

  private showLogin(message: string | null): void {
    hideShell();
    this.dropResult();
    this.closeOverlay();
    const id = h('input', { id: 'login-id', name: 'loginId', autocomplete: 'username', required: true, 'data-testid': 'login-id' });
    const pw = h('input', { id: 'login-pw', name: 'password', type: 'password', autocomplete: 'current-password', required: true, 'data-testid': 'login-pw' });
    const err = h('p', { class: 'err', role: 'alert', 'data-testid': 'login-err' }, message ?? '');
    const btn = h('button', { class: 'primary', type: 'submit', 'data-testid': 'login-submit' }, '로그인');
    const form = h('form', {
      class: 'card form-stack',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        btn.disabled = true; btn.textContent = '확인하고 있어요'; err.textContent = '';
        const out = await this.login(id.value, pw.value);
        btn.disabled = false; btn.textContent = '로그인';
        if (out.kind === 'stale') return;
        if (out.kind === 'ok') { this.afterMe(out.me); return; }
        err.textContent = out.message;
      },
    }, h('div', { class: 'field' }, h('label', { for: 'login-id' }, '아이디'), id),
    h('div', { class: 'field' }, h('label', { for: 'login-pw' }, '비밀번호'), pw), err, btn);
    clear($app());
    $app().append(card(h('h2', {}, '로그인'), form));
    id.focus();
  }

  /** A-01 ~ A-04, A-10. 기다리는 사이 세대가 바뀌면(로그아웃·계정 전환) 결과를 쓰지 않는다. */
  private async login(loginId: string, password: string): Promise<{ kind: 'ok'; me: Me } | { kind: 'err'; message: string } | { kind: 'stale' }> {
    const gen = currentGen();
    const r = await this.t.request('POST', '/auth/login', { body: { loginId, password } });
    if (gen !== currentGen()) return { kind: 'stale' };
    if (r.kind === 'network') return { kind: 'err', message: '연결을 확인한 뒤 다시 시도해 주세요.' };
    if (r.status === 401) return { kind: 'err', message: '아이디 또는 비밀번호가 맞지 않아요.' };
    if (r.status === 429) return { kind: 'err', message: lockoutMessage(r.body['retryAfterSec']) };
    if (r.status !== 200) { devlog('auth', `login_http_${r.status}`, null); return { kind: 'err', message: '잠시 문제가 생겼어요. 다시 시도해 주세요.' }; }
    const m = await this.t.request('GET', '/me');
    if (gen !== currentGen()) return { kind: 'stale' };
    if (m.kind === 'network') return { kind: 'err', message: '연결을 확인한 뒤 다시 시도해 주세요.' };
    if (m.status === 401) return { kind: 'err', message: '다시 로그인해 주세요.' };
    if (m.status !== 200) return { kind: 'err', message: '잠시 문제가 생겼어요. 다시 시도해 주세요.' };
    return { kind: 'ok', me: m.body as Me };
  }

  private afterMe(me: Me): void {
    this.tabFor(me.id);
    if (this.owner !== null && this.owner !== me.id) this.cleanupSession();
    else if (this.me !== null && this.me.id !== me.id) bumpGen(); // 계정이 바뀌면 이전 계정의 대기 응답을 버린다
    if (this.me?.id !== me.id) this.homeTab = 'home';
    this.me = me;
    this.mountNav(me);
    // DEV-I-027: 교사는 배정 운영 화면, 원장은 /ops에서 배정 운영(그 밖의 경로는 기존 문항 등록·검수)
    if ((me.role === 'teacher' || (me.role === 'director' && location.pathname.replace(/\/$/, '') === '/ops')) && me.status === 'approved') {
      mountTeacherOps($app(), this.t, me.role, () => void this.logout()); return;
    }
    if (me.role === 'director' && me.status === 'approved') { mountContentReview($app(), this.t, () => void this.logout()); return; }
    if (me.role !== 'student' || me.status !== 'approved') { this.showStatus(me); return; }
    this.showHome();
  }

  /** 공통 사이드바. 승인된 계정에만 보이며, 접고 펴도 #app은 다시 그리지 않는다. 학생의 미저장 답은 기존 나가기 확인으로 지킨다 */
  private mountNav(me: Me): void {
    if (me.status !== 'approved') { hideShell(); return; }
    const path = location.pathname.replace(/\/$/, '');
    if (me.role === 'student') {
      const home = () => this.goTab('home');
      const on = (tab: string) => this.assignmentId === null && this.homeTab === tab;
      mountShell({
        name: me.name ?? "", roleLabel: '학생', onHome: home, onLogout: () => void this.logout(),
        items: [
          { id: 'home', label: '홈 · 오늘 할 일', icon: '⌂', current: on('home'), onSelect: home },
          { id: 'learn', label: '내 학습', icon: '▤', current: on('learn'), onSelect: () => this.goTab('learn') },
          { id: 'review', label: '오답·근거 복습', icon: '↻', current: on('review'), onSelect: () => this.goTab('review') },
          { id: 'record', label: '학습 기록', icon: '☰', current: on('record'), onSelect: () => this.goTab('record') },
        ],
      });
      return;
    }
    if (me.role === 'teacher' || me.role === 'director') {
      const ops = me.role === 'teacher' || path === '/ops';
      const goMain = () => { if (location.pathname === '/' || location.pathname === '/ops') window.scrollTo({ top: 0 }); else location.assign('/'); };
      mountShell({
        name: me.name ?? "", roleLabel: me.role === 'teacher' ? '선생님' : '원장', onHome: goMain, onLogout: () => void this.logout(),
        items: [
          { id: 'ops', label: '배정·학습 운영', icon: '▤', current: ops, href: '/ops' },
          ...(me.role === 'director' ? [{ id: 'review', label: '문항 등록·검수', icon: '✎', current: !ops, href: '/' }] : []),
        ],
      });
      return;
    }
    hideShell();
  }

  /** A-06 ~ A-09 */
  private showStatus(me: Me): void {
    hideShell();
    this.dropResult();
    this.closeOverlay();
    const logout = h('button', { class: 'secondary', 'data-testid': 'logout', onclick: () => void this.logout() }, '로그아웃');
    const recheck = (label: string) => h('button', {
      class: 'primary', 'data-testid': 'recheck-status',
      onclick: async () => {
        const c = this.ctx();
        const r = await this.t.request('GET', '/me');
        if (!this.live(c)) return;
        if (r.kind === 'http' && r.status === 200) this.afterMe(r.body as Me);
        else if (r.kind === 'http' && r.status === 401) this.showLogin('다시 로그인해 주세요.');
        else toast('연결을 확인한 뒤 다시 시도해 주세요.');
      },
    }, label);
    let title: string; let body: string; let primary: HTMLElement | null = null;
    if (me.role !== 'student') { title = '학생 계정으로 로그인해 주세요'; body = '선생님 화면은 아직 준비 중이에요.'; }
    else if (me.status === 'pending') { title = '가입 승인을 기다리고 있어요'; body = '승인되면 다시 로그인해 주세요.'; primary = recheck('승인 여부 다시 확인'); }
    else if (me.status === 'rejected') { title = '가입이 승인되지 않았어요'; body = '학원에 문의해 주세요.'; }
    else { title = '지금은 학습 기능을 이용할 수 없어요'; body = '학원에 문의해 주세요.'; primary = recheck('상태 다시 확인'); }
    clear($app());
    $app().append(card(h('h2', { 'data-testid': 'status-title' }, title), h('p', {}, body), primary, logout));
  }

  private async logout(): Promise<void> {
    this.tabFor(null);
    this.cleanupSession();
    this.me = null;
    bumpGen();
    this.showLogin(null);
    await this.t.request('POST', '/auth/logout', { body: {} });
  }

  /** L-3: 이전 계정의 화면 답·대기 요청·재시도 타이머 정리, 세대 번호 올림. 서버 기록은 지우지 않는다. */
  private cleanupSession(): void {
    bumpGen();
    this.sm?.dispose();
    this.sc?.dispose();
    this.submitWait?.cancel();
    this.sm = null; this.sc = null; this.view = null; this.data = null; this.attemptId = null; this.owner = null;
    this.conflictQueue = []; this.conflictOpen = false; this.reloginOpen = false;
    this.dropResult(); // 결과 화면 값과 대기 중인 결과 요청도 정리(요청 응답은 세대·화면 확인으로 버려짐)
    this.closeOverlay();
  }

  // ---------------------------------------------------------------- 과제 목록·시작 전 정보 (3장, DEV-I-002 항목 4)

  private showHome(): void {
    void this.gateProfile().then((ok) => {
      if (!ok) return;
      if (this.assignmentId !== null) void this.showAssignment(this.assignmentId);
      else void this.showList();
    });
  }

  /** PR36: 학교·학년 프로필이 없는 학생은 첫설정을 먼저 한다(저장 전에는 목록으로 돌아가지 않음). 기존 답안·이력은 건드리지 않는다. 조회 실패는 설정 필요와 구분해 다시 시도하게 한다 */
  private async gateProfile(): Promise<boolean> {
    const c = this.ctx();
    const r = await this.t.request('GET', '/me/profile');
    if (!this.live(c)) return false;
    if (r.kind === 'http' && r.status === 401) { this.showLogin('다시 로그인해 주세요.'); return false; }
    if (r.kind !== 'http' || r.status !== 200) {
      this.screen(h('p', { class: 'notice', role: 'alert', 'data-testid': 'profile-load-error' }, '학교·학년 정보를 불러오지 못했어요.'), h('button', { class: 'primary', 'data-testid': 'profile-retry', onclick: () => this.showHome() }, '다시 불러오기'));
      return false;
    }
    if (r.body['registered'] === true) return true;
    const host = h('div');
    this.screen(host);
    mountLearningSetup(host, this.t, { onStart: (id) => this.navigate(id), onBack: () => this.navigate(null), forced: true });
    return false;
  }

  private topbar(): HTMLElement {
    return h('div', { class: 'ahead' },
      h('span', { class: 'mark', 'aria-hidden': 'true' }),
      h('span', { class: 't' }, `${this.me?.name ?? ''}`),
      h('button', { class: 'exit', 'data-testid': 'logout', onclick: () => void this.logout() }, '로그아웃'));
  }

  private screen(...children: (Node | null)[]): void {
    this.dropResult(); // 결과 화면을 떠나면 결과 값을 남기지 않는다
    this.closeOverlay();
    clear($app());
    $app().append(this.topbar(), h('div', { class: 'body' }, h('div', { class: 'list-main' }, ...children)));
  }

  private dueLine(a: AssignmentInfo): string | null { return a.dueAt === null ? null : `마감 ${formatKst(a.dueAt)} (한국 시간)`; }

  private async showList(): Promise<void> {
    this.screen(h('p', { class: 'loading' }, '불러오고 있어요'));
    const c = this.ctx();
    const r = await this.t.request('GET', '/assignments');
    if (!this.live(c) || this.assignmentId !== null) return;
    if (r.kind === 'http' && r.status === 401) { this.showLogin('다시 로그인해 주세요.'); return; }
    if (r.kind === 'http' && r.status === 403) { await this.refreshStatusScreen(); return; }
    if (r.kind !== 'http' || r.status !== 200) {
      this.screen(h('p', { class: 'notice', role: 'alert' }, '과제를 불러오지 못했어요.'), h('button', { class: 'primary', onclick: () => void this.showList() }, '다시 불러오기'));
      return;
    }
    const list = r.body['assignments'] as AssignmentInfo[];
    const rows = list.map((a) => h('button', { class: 'acard', 'data-testid': `acard-${a.id}`, onclick: () => this.navigate(a.id) },
      h('span', { class: 'at' }, a.title),
      h('span', { class: 'ameta' }, [a.release.title, this.dueLine(a)].filter(Boolean).join(' · ')),
      h('span', { class: 'ameta' }, `입력 방식: ${channelText(a.inputChannels)}`),
      h('span', { class: 'badge-status', 'data-s': a.progress.status, 'data-testid': `astatus-${a.id}` }, STATUS_LABEL[a.progress.status] ?? a.progress.status)));
    const tab = this.homeTab;
    const mats = h('div', { 'data-testid': 'home-materials-box' });
    const rtasks = h('div', { 'data-testid': 'home-review-tasks' });
    const more = (to: 'learn' | 'review' | 'record') => () => this.goTab(to);
    const MAX_HOME = 5;
    const titles = { home: '홈 · 오늘 할 일', learn: '내 학습', review: '오답·근거 복습', record: '학습 기록' } as const;
    const tabBar = h('nav', { class: 'tabbar', 'aria-label': '학습 구역', 'data-testid': 'home-tabs' },
      ...(['home', 'learn', 'review', 'record'] as const).map((k) => toggleButton(titles[k], tab === k, `home-tab-${k}`, () => this.goTab(k))));
    const heading = h('h2', { 'data-testid': 'home-title' }, titles[tab]);
    const assignmentBox = (items: HTMLElement[], total: number): HTMLElement => total === 0
      ? h('p', { 'data-testid': 'list-empty' }, '지금 풀 수 있는 과제가 없어요. 선생님이 제공한 학교 자료는 \'내 학습\'에서 바로 시작할 수 있어요.')
      : h('div', { class: 'alist', 'data-testid': 'assignment-list' }, ...items);
    const tools = h('div', { class: 'lhead' },
      h('button', { class: 'secondary', 'data-testid': 'open-words', onclick: () => this.openWords(null) }, '내 단어장'),
      h('button', { class: 'secondary', 'data-testid': 'open-setup', onclick: () => this.openSetup() }, '내 학교·학년 설정'));
    if (tab === 'home') {
      mountHomeMaterials(mats, this.t, (id) => this.navigate(id), { summary: true, onMore: more('learn') });
      mountReviewTasks(rtasks, this.t, { limit: 3, onMore: more('review') });
      const inProg = list.filter((a) => a.progress.status !== 'submitted').length;
      const goal = h('div', { 'data-testid': 'home-goal' });
      mountStudyGoal(goal, this.t, { onStart: (id) => this.navigate(id), goLearn: () => this.goTab('learn'), goReview: () => this.goTab('review') });
      this.screen(tabBar, heading, goal,
        h('p', { class: 'ev-note', 'data-testid': 'home-summary' }, `풀고 있는 과제 ${inProg}개 · 전체 과제 ${list.length}개. 학교 자료 전체 목록은 '내 학습'에서 볼 수 있어요.`),
        rtasks, mats, tools,
        h('h3', {}, '내 과제'), assignmentBox(rows.slice(0, MAX_HOME), list.length),
        list.length > MAX_HOME ? h('button', { class: 'secondary', type: 'button', 'data-testid': 'rec-all', onclick: more('record') }, `학습 기록 전체 보기 (${list.length}개)`) : null);
    } else if (tab === 'learn') {
      mountHomeMaterials(mats, this.t, (id) => this.navigate(id));
      this.screen(tabBar, heading, h('p', { class: 'ev-note' }, '학교·학년에 맞춰 선생님이 제공한 자료예요. 이어하기·시작 전·완료로 나뉘어 있어요.'), mats, tools);
    } else if (tab === 'review') {
      mountReviewTasks(rtasks, this.t, { tabs: true });
      this.screen(tabBar, heading, rtasks);
    } else {
      const rec = h('div', { 'data-testid': 'learning-record' });
      mountLearningRecord(rec, this.t, list, (id) => this.navigate(id));
      this.screen(tabBar, heading, rec);
    }
    if (this.me !== null) this.mountNav(this.me);
  }

  /** PR36: 학교·학년 첫설정과 내 자료 고르기(learning-setup.ts). 고른 자료의 풀이는 기존 과제 화면으로 이어진다 */
  private openSetup(): void {
    const host = h('div');
    this.screen(host);
    mountLearningSetup(host, this.t, { onStart: (id) => this.navigate(id), onBack: () => this.navigate(null) });
  }

  private async showAssignment(id: string, message: string | null = null): Promise<void> {
    this.screen(h('p', { class: 'loading' }, '불러오고 있어요'));
    const c = this.ctx();
    const r = await this.t.request('GET', `/assignments/${id}`);
    if (!this.live(c) || this.assignmentId !== id) return;
    const back = h('button', { class: 'secondary', 'data-testid': 'to-list', onclick: () => this.navigate(null) }, '과제 목록으로');
    if (r.kind === 'http' && r.status === 401) { this.showLogin('다시 로그인해 주세요.'); return; }
    if (r.kind === 'http' && r.status === 403) { await this.refreshStatusScreen(); return; }
    if (r.kind === 'http' && r.status === 404) { this.screen(h('p', { class: 'notice', role: 'alert', 'data-testid': 'entry-msg' }, '과제를 열 수 없어요. 링크를 다시 확인해 주세요.'), back); return; }
    if (r.kind !== 'http' || r.status !== 200) {
      this.screen(h('p', { class: 'notice', role: 'alert', 'data-testid': 'entry-msg' }, '과제를 불러오지 못했어요.'), h('button', { class: 'primary', onclick: () => void this.showAssignment(id) }, '다시 불러오기'), back);
      return;
    }
    const a = r.body as AssignmentInfo;
    const action = a.action === 'start'
      ? h('button', { class: 'primary', 'data-testid': 'start', onclick: (e: Event) => void this.start(e.currentTarget as HTMLButtonElement) }, '풀기 시작')
      : a.action === 'resume'
        ? h('button', { class: 'primary', 'data-testid': 'start', onclick: (e: Event) => void this.start(e.currentTarget as HTMLButtonElement) }, '이어 풀기')
        : null;
    const done = a.progress.status === 'submitted'
      ? h('p', { class: 'notice-ok', 'data-testid': 'submitted-note' }, `이 과제는 제출했어요${a.progress.submittedAt !== null ? ` (${formatKst(a.progress.submittedAt)}, 한국 시간)` : ''}. 제출한 답은 바꿀 수 없어요.`)
      : a.progress.status === 'void' && a.action === 'none' ? h('p', { class: 'notice' }, '이 풀이는 더 이상 이어갈 수 없어요. 선생님께 문의해 주세요.') : null;
    this.screen(
      h('h2', { 'data-testid': 'info-title' }, a.title),
      message !== null ? h('p', { class: 'notice', role: 'alert', 'data-testid': 'entry-msg' }, message) : null,
      h('dl', { class: 'info' },
        h('dt', {}, '문제지'), h('dd', {}, a.release.title),
        a.release.sourceLabel !== null ? h('dt', {}, '자료 출처') : null, a.release.sourceLabel !== null ? h('dd', {}, a.release.sourceLabel) : null,
        h('dt', {}, '마감'), h('dd', { 'data-testid': 'info-due' }, a.dueAt === null ? '마감 없음' : `${formatKst(a.dueAt)} (한국 시간)`),
        h('dt', {}, '진행 상태'), h('dd', { 'data-testid': 'info-status' }, STATUS_LABEL[a.progress.status] ?? a.progress.status),
        h('dt', {}, '입력 방식'), h('dd', { 'data-testid': 'info-channels' }, channelText(a.inputChannels)),
        // 풀이 화면과 같은 안내 규칙(core/guide.ts). 응답 방식은 서버가 배포 판에서 계산한 요약(DEV-I-004)
        h('dt', {}, '제출 조건'), h('dd', { 'data-testid': 'info-unanswered' }, unansweredGuide(a.allowUnanswered, a.release.responseModes ?? null))),
      done,
      // DEV-I-007: 제출한 과제는 그 시도(progress.attemptId)의 결과를 본다. 새 시도를 만들지 않는다.
      a.progress.status === 'submitted' && a.progress.attemptId !== null
        ? h('button', { class: 'primary', 'data-testid': 'open-result', 'data-att': a.progress.attemptId, onclick: () => this.openResult(a.id, a.progress.attemptId!) }, '결과 보기') : null,
      action, back);
  }

  private async start(btn: HTMLButtonElement): Promise<void> {
    const id = this.assignmentId!;
    btn.disabled = true;
    const c = this.ctx();
    const r = await this.t.request('POST', `/assignments/${id}/attempts`, { body: {} });
    if (!this.live(c) || this.assignmentId !== id) return;
    btn.disabled = false;
    if (r.kind === 'network') { await this.showAssignment(id, '과제를 불러오지 못했어요.'); return; }
    if (r.status === 401) { this.showLogin('다시 로그인해 주세요.'); return; }
    if (r.status === 403) { await this.refreshStatusScreen(); return; }
    if (r.status === 404) { await this.showAssignment(id); return; }
    // 제출한 과제: 새 시도를 만들지 않는다(재응시 허용 기능 없음). 제출 완료 상태를 안내한다.
    if (r.status === 409 && r.body['error'] === 'already_submitted') { await this.showAssignment(id); return; }
    // 정답 기준이 아직 준비되지 않은 자료는 시작 전에 막힌다. 학생이 풀고 나서 교사 승인을 기다리게 하지 않는다.
    if (r.status === 409 && r.body['error'] === 'answer_key_not_ready') {
      await this.showAssignment(id, '이 자료는 아직 정답 준비가 끝나지 않아 지금은 풀 수 없어요. 선생님께 준비가 끝났는지 물어봐 주세요.'); return;
    }
    if (r.status !== 200 && r.status !== 201) { devlog('other', `start_http_${r.status}`, null); await this.showAssignment(id, '잠시 문제가 생겼어요. 다시 시도해 주세요.'); return; }
    await this.loadAttempt(String(r.body['attemptId']), r.body['resumed'] === true);
  }

  private async refreshStatusScreen(): Promise<void> {
    const c = this.ctx();
    const m = await this.t.request('GET', '/me');
    if (!this.live(c)) return;
    if (m.kind === 'http' && m.status === 200) { this.cleanupSession(); this.afterMe(m.body as Me); }
    else { this.cleanupSession(); this.showLogin('다시 로그인해 주세요.'); }
  }

  private async loadAttempt(attemptId: string, resumed: boolean): Promise<void> {
    clear($app());
    $app().append(this.topbar(), h('p', { class: 'loading' }, '불러오고 있어요'), h('div', { class: 'list-main' }, h('button', { class: 'secondary', 'data-testid': 'to-list', onclick: () => this.navigate(null) }, '과제 목록으로')));
    const c = this.ctx();
    const assignmentId = this.assignmentId;
    const r = await this.t.request('GET', `/attempts/${attemptId}/items`);
    // 기다리는 사이 계정·화면이 바뀌었으면 이 응답으로 화면을 만들지 않는다(디자인 v0.4.1 R2a)
    if (!this.live(c) || this.assignmentId !== assignmentId || this.sm !== null) return;
    if (r.kind === 'network') { await this.showAssignment(assignmentId!, '과제를 불러오지 못했어요.'); return; }
    if (r.status === 401) { this.showLogin('다시 로그인해 주세요.'); return; }
    if (r.status === 403) { await this.refreshStatusScreen(); return; }
    if (r.status !== 200 || r.body['attemptId'] !== attemptId) { devlog('items', `http_${r.status}:${String(r.body['error'] ?? '')}`, null); await this.showAssignment(assignmentId!, '잠시 문제가 생겼어요. 다시 시도해 주세요.'); return; }
    const data = r.body as ItemsResponse;
    if (data.status === 'submitted') { this.showClosed('already', 0, null, attemptId); return; }
    if (data.status === 'void') { this.showClosed('void', 0); return; }
    if (data.items.length === 0) {
      clear($app());
      $app().append(card(h('h2', {}, '이 과제에 문항이 없어요'), h('p', {}, '선생님께 알려 주세요.'), h('button', { class: 'primary', onclick: () => this.navigate(null) }, '과제 목록으로')));
      return;
    }
    this.attemptId = attemptId;
    this.data = data;
    this.owner = this.me!.id;
    this.sm = new SaveManager({ transport: this.t, attemptId, deviceId: this.deviceId, newKey: newRequestKey, emit: (e) => this.onSave(e) });
    for (const it of data.items) {
      if (it.responseMode === 'single-choice') this.sm.add(it.id, 'choice', this.serverValue(data, it.id, 'choice'));
      else for (const p of it.grPoints) this.sm.add(p.id, 'gr', this.serverValue(data, p.id, 'gr'));
    }
    this.sc = new SubmitController({ transport: this.t, attemptId, newKey: newRequestKey, emit: (e) => this.onSubmitEvent(e), timing: this.timing });
    this.view = new SolveView(data, this.sm, {
      onSubmit: () => void this.onSubmitClick(),
      onExit: () => this.onExit(),
      onRetry: () => this.sm?.retryNow(),
      onOpenConflicts: () => this.openNextConflict(),
    });
    clear($app());
    $app().append(this.view.root);
    if (resumed) toast('저장된 답을 불러왔어요.', 3000);
  }

  private serverValue(data: ItemsResponse, id: string, kind: 'gr' | 'choice'): ServerValue {
    if (kind === 'gr') {
      const g = data.grSaved.find((x) => x.pointId === id);
      return g === undefined ? { value: null, revision: 0 } : { value: grValueFromSaved(g), revision: g.revision };
    }
    const s = data.saved.find((x) => x.itemId === id);
    return s === undefined ? { value: { kind: 'blank' }, revision: 0 } : { value: choiceFromSaved(s.state, s.value, s.omrRaw, s.channel), revision: s.revision };
  }

  // ---------------------------------------------------------------- 저장 사건

  private onSave(e: SaveEvent): void {
    if (e.type === 'changed') { this.view?.updateTarget(e.id); return; }
    if (e.type === 'conflict' || e.type === 'keyconflict') { this.enqueueConflict(e.id); return; }
    if (e.type === 'unauthorized') { this.openRelogin(); return; }
    if (e.type === 'forbidden') { void this.refreshStatusScreen(); return; }
    if (e.type === 'attempt-closed') { this.sm?.lock(); this.showClosed(e.status === 'submitted' ? 'already' : 'void', 0); return; }
    if (e.type === 'server-error') toast('잠시 문제가 생겼어요. 다시 시도해 주세요.');
  }

  private hasUnsaved(): boolean {
    if (this.sm === null) return false;
    const c = this.sm.counts();
    return c.dirty + c.saving + c.failed + c.conflict > 0;
  }

  // ---------------------------------------------------------------- 충돌 확인 (6장·4-3)

  private enqueueConflict(id: string): void {
    if (!this.conflictQueue.includes(id)) this.conflictQueue.push(id);
    if (!this.conflictOpen && !this.reloginOpen && !(this.sc?.locksInput() ?? false)) this.openNextConflict();
  }

  private openNextConflict(): void {
    if (this.sm === null || this.conflictOpen || this.reloginOpen) return;
    const id = this.conflictQueue.find((x) => this.sm!.get(x).flag === 'conflict' || this.sm!.get(x).flag === 'keyconflict')
      ?? this.sm.all().find((t) => t.flag === 'conflict' || t.flag === 'keyconflict')?.id;
    this.conflictQueue = this.conflictQueue.filter((x) => x !== id);
    if (id === undefined) return;
    void this.showConflict(id);
  }

  /** 서버의 지금 값·수정 번호 읽기(C-1·C-3·K-2). 문맥이 바뀌었으면 'stale' */
  private async readServer(id: string): Promise<{ kind: 'ok'; server: ServerValue; status: string } | { kind: 'fail'; r: ApiResult } | { kind: 'stale' }> {
    const c = this.ctx();
    const r = await this.t.request('GET', `/attempts/${this.attemptId}/items`);
    if (!this.live(c) || this.sm === null) return { kind: 'stale' };
    if (r.kind !== 'http' || r.status !== 200 || r.body['attemptId'] !== this.attemptId) return { kind: 'fail', r };
    const data = r.body as ItemsResponse;
    const kind = this.sm.get(id).kind;
    return { kind: 'ok', server: this.serverValue(data, id, kind), status: data.status };
  }

  private async showConflict(id: string): Promise<void> {
    const sm = this.sm!; const view = this.view!;
    this.conflictOpen = true;
    const t = sm.get(id);
    const isKey = t.flag === 'keyconflict';
    const msg = h('p', { class: 'notice', hidden: true, 'data-testid': 'conflict-msg' });
    const mine = h('b', { 'data-testid': 'conflict-mine' });
    const theirs = h('b', { 'data-testid': 'conflict-server' });
    const render = () => {
      mine.textContent = view.valueLabel(id, sm.get(id).screen);
      const seen = sm.get(id).seen;
      theirs.textContent = seen === null ? '확인하고 있어요' : view.valueLabel(id, seen.value);
    };
    const say = (s: string) => { msg.hidden = false; msg.textContent = s; };
    let decisionKey: string | null = null;
    const keep = h('button', {
      class: 'primary', 'data-testid': 'conflict-keep',
      onclick: async () => {
        keep.disabled = true; take.disabled = true;
        decisionKey ??= newRequestKey(); // 학생의 이 결정에 키 하나. 결과를 못 받고 다시 누르면 같은 키.
        const out = await sm.keepScreen(id, decisionKey);
        if (this.sm !== sm) return;
        keep.disabled = false; take.disabled = false;
        if (out.kind === 'saved') { done(); return; }
        if (out.kind === 'network') { say('저장 결과를 받지 못했어요. 연결을 확인한 뒤 다시 눌러 주세요.'); return; }
        decisionKey = null;
        if (out.kind === 'stale') { render(); say('그 사이 저장된 답이 다시 바뀌었어요. 바뀐 답을 보고 다시 골라 주세요.'); return; }
        if (out.kind === 'closed') { closeDialog(); this.sm?.lock(); this.showClosed(out.status === 'submitted' ? 'already' : 'void', 0, '이미 제출돼서 답을 바꿀 수 없어요.'); return; }
        if (out.kind === 'unauthorized') { closeDialog(); return; }
        say('잠시 문제가 생겼어요. 다시 시도해 주세요.');
      },
    }, '이 화면의 답으로 저장');
    const take = h('button', {
      class: 'secondary', 'data-testid': 'conflict-take',
      onclick: async () => {
        keep.disabled = true; take.disabled = true;
        const r = await this.readServer(id);
        if (r.kind === 'stale') return;
        keep.disabled = false; take.disabled = false;
        if (r.kind === 'fail') { if (r.r.kind === 'http' && r.r.status === 401) { closeDialog(); sm.onUnauthorized(); return; } say('저장된 답을 확인하지 못했어요. 고른 답은 이 화면에 남아 있어요.'); return; }
        if (r.status !== 'in-progress') { closeDialog(); this.sm?.lock(); this.showClosed(r.status === 'submitted' ? 'already' : 'void', 0, '이미 제출돼서 답을 바꿀 수 없어요.'); return; }
        const seen = sm.get(id).seen;
        if (seen !== null && seen.revision === r.server.revision && sameValue(seen.value, r.server.value)) { sm.takeServer(id, r.server); done(); return; }
        sm.setSeen(id, r.server); // C-3b
        render();
        say('그 사이 저장된 답이 다시 바뀌었어요. 바뀐 답을 보고 다시 골라 주세요.');
      },
    }, '저장된 답으로 바꾸기');
    const closeDialog = () => { this.conflictOpen = false; this.closeOverlay(); };
    const done = () => { closeDialog(); this.openNextConflict(); };
    this.openOverlay(sheet('conflict',
      h('h2', {}, isKey ? '이 답의 자동 저장을 멈췄어요' : '다른 기기에서 저장한 답이 있어요'),
      isKey ? h('p', {}, `${view.targetName(id)}의 저장 요청을 확인하다 문제가 생겼어요. 저장된 답을 보고 남길 답을 골라 주세요.`) : null,
      h('p', { 'data-testid': 'conflict-where' }, view.targetName(id)),
      h('div', { class: 'ctx' }, view.contextOf(id)),
      h('div', { class: 'cmp' },
        h('div', {}, h('small', {}, '이 화면의 답'), mine),
        h('div', {}, h('small', {}, '저장된 답'), theirs)),
      msg,
      h('div', { class: 'acts' }, keep, take)));
    render();
    if (sm.get(id).seen === null) {
      keep.disabled = true; take.disabled = true;
      const r = await this.readServer(id);
      if (r.kind === 'stale') return;
      keep.disabled = false; take.disabled = false;
      if (r.kind === 'ok') { sm.setSeen(id, r.server); render(); }
      else say('저장된 답을 확인하지 못했어요. 고른 답은 이 화면에 남아 있어요.');
    }
  }

  // ---------------------------------------------------------------- 재로그인과 복귀 (5장)

  private openRelogin(): void {
    if (this.reloginOpen) return;
    this.reloginOpen = true;
    this.conflictOpen = false;
    const unsaved = this.sm === null ? 0 : (() => { const c = this.sm!.counts(); return c.dirty + c.saving + c.failed + c.conflict; })();
    const uncertain = this.sc?.uncertain() ?? false;
    const id = h('input', { id: 're-id', autocomplete: 'username', required: true, 'data-testid': 'login-id', value: '' });
    const pw = h('input', { id: 're-pw', type: 'password', autocomplete: 'current-password', required: true, 'data-testid': 'login-pw' });
    const err = h('p', { class: 'err', role: 'alert', 'data-testid': 'login-err' });
    const btn = h('button', { class: 'primary', type: 'submit', 'data-testid': 'login-submit' }, '로그인');
    const form = h('form', {
      class: 'form-stack',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        btn.disabled = true; btn.textContent = '확인하고 있어요'; err.textContent = '';
        const out = await this.login(id.value, pw.value);
        btn.disabled = false; btn.textContent = '로그인';
        if (out.kind === 'stale') return;
        if (out.kind === 'err') { err.textContent = out.message; return; }
        await this.afterRelogin(out.me);
      },
    }, h('div', { class: 'field' }, h('label', { for: 're-id' }, '아이디'), id), h('div', { class: 'field' }, h('label', { for: 're-pw' }, '비밀번호'), pw), err, btn);
    this.openOverlay(sheet('relogin',
      h('h2', {}, '다시 로그인해 주세요'),
      h('p', {}, this.quiz !== null && this.sm === null ? '로그인이 끝나서 시험을 확인하지 못했어요. 같은 아이디로 로그인하면 같은 요청으로 이어서 확인해요.'
        : this.words !== null && this.sm === null ? '로그인이 끝나서 단어장을 확인하지 못했어요. 같은 아이디로 로그인하면 보던 화면을 다시 확인해요.'
        : this.evidence !== null && this.sm === null ? '로그인이 끝나서 확인하지 못했어요. 같은 아이디로 로그인하면 이어서 확인해요.'
        : this.result !== null && this.sm === null ? '로그인이 끝나서 결과를 확인하지 못했어요. 같은 아이디로 로그인하면 이 결과를 다시 확인해요.' : '이 화면에서 고른 답은 그대로 남아 있어요.'),
      this.evidence !== null && this.submitter.all().length > 0 ? h('p', { 'data-testid': 'relogin-pending' }, '제출됐는지 확인하지 못한 근거 응답이 있어요. 같은 아이디로 로그인하면 같은 답으로 확인해요. 다른 아이디로 로그인하면 이 탭의 고른 보기는 사라져요.') : null,
      unsaved > 0 ? h('p', { 'data-testid': 'relogin-unsaved' }, '다른 아이디로 로그인하거나 창을 닫으면 저장되지 않은 답은 사라져요.') : null,
      uncertain ? h('p', { 'data-testid': 'relogin-uncertain' }, '제출 결과를 아직 확인하지 못했어요. 로그인하면 그것부터 확인할게요.') : null,
      form));
    id.focus();
  }

  private progress(text: string, retry: (() => void) | null = null): void {
    this.openOverlay(sheet('relogin-progress', h('h2', {}, text), retry !== null
      ? h('div', { class: 'acts' }, h('button', { class: 'primary', 'data-testid': 'relogin-retry', onclick: retry }, '다시 확인')) : null));
  }

  private async afterRelogin(me: Me): Promise<void> {
    // 단어 시험(DEV-I-020): 같은 계정이면 보던 화면을 다시 조회하고 미확정 제출은 같은 키·같은 내용으로 이어서 확인, 다른 계정이면 탭 값을 지우고 그 계정의 첫 화면
    if (this.quiz !== null && this.sm === null) {
      this.reloginOpen = false;
      this.closeOverlay();
      if (this.owner !== null && me.id === this.owner && me.role === 'student' && me.status === 'approved') {
        this.me = me;
        const S = this.quiz;
        void this.loadQuiz().then(() => { if (this.quiz === S && S.view === 'play' && this.quizTab.pending !== null) void this.sendPending(S); });
        return;
      }
      this.tabFor(me.id);
      this.cleanupSession(); this.assignmentId = null;
      if (location.pathname !== '/') history.pushState(null, '', '/');
      this.afterMe(me); return;
    }
    // 내 단어장(DEV-I-013): 같은 계정이면 보던 화면(목록·상세)을 다시 조회, 다른 계정이면 값을 지우고 그 계정의 첫 화면
    if (this.words !== null && this.sm === null) {
      this.reloginOpen = false;
      this.closeOverlay();
      if (this.owner !== null && me.id === this.owner && me.role === 'student' && me.status === 'approved') { this.me = me; void this.loadWords(); return; }
      this.cleanupSession(); this.assignmentId = null;
      if (location.pathname !== '/') history.pushState(null, '', '/');
      this.afterMe(me); return;
    }
    // 근거 문제 화면(DEV-I-010 EV-5): 같은 계정이면 다시 조회하고 미확정 제출을 같은 키로 이어서 확인, 다른 계정이면 탭 값을 지우고 그 계정의 첫 화면
    if (this.evidence !== null && this.sm === null) {
      this.reloginOpen = false;
      this.closeOverlay();
      if (this.owner !== null && me.id === this.owner && me.role === 'student' && me.status === 'approved') {
        this.me = me; this.submitter.resumeAll(); void this.loadEvidence('relogin'); return;
      }
      this.tabFor(me.id);
      this.cleanupSession(); this.assignmentId = null;
      if (location.pathname !== '/') history.pushState(null, '', '/');
      this.afterMe(me); return;
    }
    // 결과 화면(DEV-I-007 R-7): 같은 계정이면 그 시도 결과를 다시 조회, 다른 계정이면 결과 값을 지우고 그 계정의 첫 화면
    if (this.result !== null && this.sm === null) {
      this.reloginOpen = false;
      this.closeOverlay();
      if (this.owner !== null && me.id === this.owner && me.role === 'student' && me.status === 'approved') { this.me = me; void this.loadResult(); return; }
      this.cleanupSession(); this.assignmentId = null;
      if (location.pathname !== '/') history.pushState(null, '', '/');
      this.afterMe(me); return;
    }
    // R-3: 다른 계정이면 이전 계정의 화면 답·대기 요청·타이머를 정리하고 그 계정의 첫 화면으로
    if (this.owner === null || me.id !== this.owner) { this.cleanupSession(); this.afterMe(me); return; }
    this.me = me;
    if (me.role !== 'student' || me.status !== 'approved') { this.cleanupSession(); this.showStatus(me); return; }
    const sc = this.sc!;
    // R-4: 결과 미확정 제출을 먼저 확인한다. 확정될 때까지 저장을 다시 시작하지 않는다.
    if (sc.phase === 'relogin') {
      this.reloginOpen = false;
      this.progress('제출됐는지 확인하고 있어요');
      sc.afterRelogin();
      return; // 이후는 제출 사건(onSubmitEvent)이 이어 간다. 거부(7-3)로 확정되면 그때 저장 대조를 한다.
    }
    await this.reconcileSaves();
  }

  private async reconcileSaves(): Promise<void> {
    const sm = this.sm!;
    const c = this.ctx();
    const stale = () => !this.live(c) || this.sm !== sm;
    this.progress('저장된 답과 맞춰 보고 있어요');
    // R-5: 결과 미확정 저장 요청을 같은 키·같은 내용으로 다시 보내 이전 요청 결과를 얻는다.
    const prev = new Map<string, PrevResult>();
    for (const t of sm.all()) {
      if (t.unresolved === null) { prev.set(t.id, { kind: 'none' }); continue; }
      const r = await sm.replayUnresolved(t.id);
      if (stale()) return;
      if (r === 'network') { this.progress('저장된 답을 확인하지 못했어요. 고른 답은 이 화면에 남아 있어요.', () => void this.reconcileSaves()); return; }
      if (r === 'none') prev.set(t.id, { kind: 'none' });
      else if (r.ok) prev.set(t.id, { kind: 'ok', value: r.value, revision: r.revision });
      else if (r.code === 'request_key_reused') prev.set(t.id, { kind: 'keyreused' });
      else if (r.status === 401) { this.reloginOpen = false; this.openRelogin(); return; }
      else prev.set(t.id, { kind: 'none' });
    }
    // R-6: 최신 서버 값·수정 번호
    const r = await this.t.request('GET', `/attempts/${this.attemptId}/items`);
    if (stale()) return;
    if (r.kind !== 'http' || r.status !== 200 || r.body['attemptId'] !== this.attemptId) {
      if (r.kind === 'http' && r.status === 401) { this.reloginOpen = false; this.openRelogin(); return; }
      this.progress('저장된 답을 확인하지 못했어요. 고른 답은 이 화면에 남아 있어요.', () => void this.reconcileSaves());
      return;
    }
    const data = r.body as ItemsResponse;
    this.reloginOpen = false;
    // R-8: 이미 제출됨
    if (data.status !== 'in-progress') {
      const lost = sm.all().filter((t) => !sameValue(t.screen, this.serverValue(data, t.id, t.kind).value)).length;
      sm.lock();
      this.showClosed(data.status === 'submitted' ? 'already' : 'void', lost);
      return;
    }
    // R-7: 대상마다 대조
    let conflicts = 0; let pending = 0;
    for (const t of sm.all()) {
      const d = reconcileTarget(t.screen, t.confirmed, prev.get(t.id) ?? { kind: 'none' }, this.serverValue(data, t.id, t.kind));
      sm.applyReconcile(t.id, d);
      if (d.action === 'conflict' || d.action === 'keyconflict') { conflicts++; this.conflictQueue.push(t.id); }
      if (d.action === 'continue' && !sameValue(t.screen, d.confirmed.value)) pending++;
    }
    this.closeOverlay();
    sm.resume();
    // R-9
    if (conflicts > 0) { toast(`다른 기기에서 바뀐 답이 ${conflicts}개 있어요. 남길 답을 골라 주세요.`, 4000); this.openNextConflict(); }
    else if (pending > 0) toast(`다시 로그인했어요. 저장 전 답 ${pending}개를 저장하고 있어요.`, 4000);
    else toast('다시 로그인했어요.');
  }

  // ---------------------------------------------------------------- 제출 (7장)

  private async onSubmitClick(): Promise<void> {
    const sm = this.sm; const sc = this.sc; const data = this.data;
    if (sm === null || sc === null || data === null || sc.locksInput()) return;
    sm.flushAll();
    const input = () => ({ items: data.items, allowUnanswered: data.assignment.allowUnanswered, status: (id: string) => sm.status(id), value: (id: string) => sm.get(id).screen });
    let r = precheck(input());
    if (r.kind === 'wait') {
      let cancelled = false;
      this.submitWait = { cancel: () => { cancelled = true; } };
      this.openOverlay(sheet('submit-wait', h('h2', {}, '답을 저장한 뒤 제출할게요'),
        h('div', { class: 'acts' }, h('button', { class: 'secondary', onclick: () => { cancelled = true; this.closeOverlay(); } }, '취소'))));
      await sm.whenSettled(10000);
      this.submitWait = null;
      if (cancelled || this.sm !== sm) return;
      r = precheck(input());
      if (r.kind === 'wait') {
        const unsaved = sm.all().filter((t) => ['dirty', 'saving', 'failed', 'conflict'].includes(sm.status(t.id))).map((t) => t.id);
        r = { kind: 'blocked', unsaved, unselectedPoints: [], inputErrors: [], unanswered: [] };
      }
    }
    if (r.kind === 'blocked') { this.showBlocked('아직 제출할 수 없어요', r.unsaved, r.unselectedPoints, r.inputErrors, r.unanswered, true); return; }
    const count = r.unansweredCount;
    this.openOverlay(sheet('confirm',
      h('h2', {}, '제출하면 답을 바꿀 수 없어요. 제출할까요?'),
      count > 0 ? h('p', { 'data-testid': 'confirm-unanswered' }, `답하지 않은 문항이 ${count}개 있어요.`) : null,
      h('div', { class: 'acts' },
        h('button', { class: 'primary', 'data-testid': 'confirm-submit', onclick: () => { this.closeOverlay(); sc.start(); } }, '제출하기'),
        h('button', { class: 'secondary', onclick: () => this.closeOverlay() }, '계속 풀기'))));
  }

  private showBlocked(title: string, unsaved: readonly string[], unselected: readonly string[], inputErrors: readonly string[], unanswered: readonly string[], local: boolean): void {
    const view = this.view!;
    const group = (label: string, ids: readonly string[], testid: string, extra: HTMLElement | null = null) => ids.length === 0 ? null
      : h('div', { class: 'g', 'data-testid': testid }, h('b', {}, `${label} ${ids.length}`),
        h('div', {}, ...ids.map((id) => h('button', { onclick: () => { this.closeOverlay(); view.focusTarget(id, true); } }, view.targetName(id))), extra));
    this.openOverlay(sheet('blocked',
      h('h2', {}, title),
      h('div', { class: 'rej' },
        group('저장되지 않은 답', unsaved, 'blk-unsaved',
          h('button', { onclick: () => { this.closeOverlay(); this.sm?.retryNow(); } }, '다시 저장')),
        unsaved.length > 0 ? h('p', {}, '저장한 뒤 제출할 수 있어요.') : null,
        group('아직 고르지 않은 지점', unselected, 'blk-unselected'),
        group('다시 확인할 입력', inputErrors, 'blk-input'),
        group('답하지 않은 문항', unanswered, 'blk-unanswered')),
      h('div', { class: 'acts' }, h('button', { class: 'secondary', 'data-testid': 'blocked-close', onclick: () => { this.closeOverlay(); if (!local) this.sc?.dismissRejection(); } }, '계속 풀기'))));
  }

  private onSubmitEvent(e: SubmitEvent): void {
    const sc = this.sc; const sm = this.sm; const view = this.view;
    if (sc === null || sm === null || view === null) return;
    if (e.type === 'unauthorized') { sm.onUnauthorized(); this.openRelogin(); return; }
    if (e.type === 'forbidden') { void this.refreshStatusScreen(); return; }
    const p = e.phase;
    view.lockLabel = p === 'done' || p === 'done-confirmed' || p === 'already' ? '제출 완료' : '제출 확인 중';
    if (sc.locksInput()) sm.lock();
    else if (sm.isLocked()) sm.unlock();
    if (this.reloginOpen && p !== 'relogin') this.reloginOpen = false;
    switch (p) {
      case 'idle': this.closeOverlay(); return;
      case 'submitting':
        this.openOverlay(sheet('submitting', h('h2', {}, '제출하고 있어요'), h('div', { class: 'acts' }, h('button', { class: 'primary', disabled: true }, '제출하기'))));
        return;
      case 'checking':
        this.openOverlay(sheet('checking', h('h2', {}, '제출됐는지 확인하고 있어요'),
          h('p', {}, '제출 결과를 아직 받지 못했어요. 같은 제출 요청으로 결과를 확인하는 중이에요. 확인될 때까지 답은 바꿀 수 없어요.')));
        return;
      case 'stalled':
        this.openOverlay(sheet('stalled', h('h2', {}, '제출됐는지 아직 확인하지 못했어요'),
          h('p', {}, '확인될 때까지 답은 바꿀 수 없어요. 연결을 확인한 뒤 다시 확인해 주세요.'),
          h('div', { class: 'acts' },
            h('button', { class: 'primary', 'data-testid': 'recheck', onclick: () => sc.recheck() }, '다시 확인'),
            h('button', { class: 'secondary', 'data-testid': 'stalled-exit', onclick: () => this.onExit() }, '나가기'))));
        return;
      case 'relogin': return; // 재로그인 창은 unauthorized 사건이 연다
      case 'rejected': {
        const r = sc.rejection!;
        this.showBlocked('아직 제출할 수 없어요', [], r.unselectedGrPointIds, r.inputErrorItemIds, r.unansweredItemIds, false);
        void this.maybeReconcileAfterRejected();
        return;
      }
      case 'done': case 'done-confirmed': case 'already': case 'void':
        this.showClosed(p, 0);
        return;
      case 'forbidden': return;
    }
  }

  /** R-4 뒤 제출 거부로 확정된 경우: 미뤘던 저장 대조를 이어서 한다 */
  private async maybeReconcileAfterRejected(): Promise<void> {
    const sm = this.sm;
    if (sm === null || !sm.isPaused()) return;
    await this.reconcileSaves();
  }

  /** 7-5·7-5b·7-6·7-7 */
  private showClosed(kind: 'done' | 'done-confirmed' | 'already' | 'void', lost: number, lead: string | null = null, attemptOverride: string | null = null): void {
    // DEV-I-007: 결과 보기에 쓸 시도·과제는 화면 값을 지우기 전에 잡아 둔다(제출한 바로 그 시도)
    const resultAttempt = attemptOverride ?? this.attemptId;
    const resultAssignment = this.assignmentId;
    bumpGen(); // 이 시도의 남은 요청 응답은 이후 화면에 쓰지 않는다
    this.sm?.dispose(); this.sc?.dispose();
    this.sm = null; this.sc = null; this.view = null; this.data = null; this.attemptId = null; this.owner = null;
    this.conflictQueue = []; this.conflictOpen = false; this.reloginOpen = false;
    this.closeOverlay();
    const toInfo = h('button', { class: 'primary', 'data-testid': 'home', onclick: () => this.navigate(this.assignmentId) }, '과제 정보로');
    const toList = h('button', { class: 'secondary', 'data-testid': 'to-list', onclick: () => this.navigate(null) }, '과제 목록으로');
    const title = kind === 'done' ? '제출했어요' : kind === 'done-confirmed' ? '제출이 확인됐어요' : kind === 'already' ? '이 과제는 이미 제출됐어요.' : '이 풀이는 더 이상 이어갈 수 없어요.';
    const body = kind === 'done-confirmed' ? '제출한 답은 바꿀 수 없어요.' : kind === 'void' ? '선생님께 문의해 주세요.' : kind === 'already' && lost > 0 ? `제출 뒤에 고른 답 ${lost}개는 반영되지 않았어요.` : null;
    clear($app());
    const canResult = kind !== 'void' && resultAttempt !== null;
    const note = canResult ? h('p', { 'data-testid': 'done-grading-note' }, '제출하면 서버가 바로 채점해요. 결과 화면에서 맞음·틀림을 볼 수 있어요. 정답 기준이 아직 준비되지 않은 지점은 결과 대기로 보여요.') : null;
    const toResult = canResult ? h('button', { class: 'primary', 'data-testid': 'open-result', 'data-att': resultAttempt, onclick: () => this.openResult(resultAssignment, resultAttempt!) }, '결과 보기') : null;
    if (canResult) toInfo.className = 'secondary';
    $app().append(card(lead !== null ? h('p', { class: 'notice' }, lead) : null, h('h2', { 'data-testid': 'closed-title', 'data-kind': kind }, title), body !== null ? h('p', {}, body) : null, note, toResult, toInfo, toList));
  }

  // ---------------------------------------------------------------- GR 결과 (DEV-I-007, 디자인 상태표 R장)

  /** 결과 화면 열기: 화면 값을 새로 만든 뒤 조회한다(R-0). 채점은 실행하지 않는다(원장 수동 채점). */
  private openResult(assignmentId: string | null, attemptId: string): void {
    this.closeOverlay();
    this.assignmentId = assignmentId;
    this.dropResult();
    this.result = blankResult(assignmentId, attemptId);
    this.owner = this.me?.id ?? null;
    this.renderResultScreen();
    void this.loadResult();
  }

  private renderResultScreen(): void {
    const S = this.result;
    if (S === null) return;
    clear($app());
    $app().append(renderResult(S, {
      refresh: () => void this.loadResult(),
      toList: () => this.navigate(null),
      toInfo: S.assignmentId !== null ? () => this.navigate(S.assignmentId) : null,
      toEvidence: () => this.openEvidence(S.assignmentId, S.attemptId),
      toReview: () => this.openReview(S.assignmentId, S.attemptId),
      toDetail: () => this.openDetail(S.assignmentId, S.attemptId),
    }));
  }

  /** 제출 후 정답·해설 보기: 서버가 본인 제출 시도와 최초 채점 기준을 확인해 보낸 값을 펼쳐 보여 준다 */
  private openDetail(assignmentId: string | null, attemptId: string): void {
    this.closeOverlay();
    this.dropResult();
    this.assignmentId = assignmentId;
    this.owner = this.me?.id ?? null;
    const host = h('div');
    this.screen(host);
    mountResultDetail(host, this.t, attemptId, () => this.openResult(assignmentId, attemptId));
  }

  /** 오답·근거 다시 풀기: 서버가 계산한 복습 상태로 그린다. 근거는 기존 근거 문제 화면에서 푼다 */
  private openReview(assignmentId: string | null, attemptId: string): void {
    this.closeOverlay();
    this.dropResult();
    this.assignmentId = assignmentId;
    this.owner = this.me?.id ?? null;
    const host = h('div');
    this.screen(host);
    mountReview(host, this.t, attemptId, {
      onBack: () => this.openResult(assignmentId, attemptId),
      onEvidence: () => this.openEvidence(assignmentId, attemptId),
    });
  }

  /** 결과 화면 값을 버리고, 그 화면의 진행 중인 조회(GET)를 취소한다. 취소해도 서버가 이미 처리했을 수 있다(제공 기록) */
  private dropResult(): void {
    this.responseClock.pause();
    this.resultAbort?.abort();
    this.resultAbort = null;
    this.result = null;
    // 근거 문제 화면도 같은 때 떠난다: 진행 중 조회(GET)만 취소. 제출(POST)·미확정 기록은 탭에 남는다
    this.evAbort?.abort();
    this.evAbort = null;
    this.evidence = null;
    // 내 단어장도 같은 때 떠난다: 진행 중 GET 취소. 늦은 응답은 취소와 별개로 화면 값·요청 번호 확인으로 버린다
    this.wordsAbort?.abort();
    this.wordsAbort = null;
    this.words = null;
    // 단어 시험도 같은 때 떠난다: 진행 중 GET만 취소. 제출·시작(POST)은 취소하지 않고 미확정 기록은 탭에 남는다
    this.quizAbort?.abort();
    this.quizAbort = null;
    this.quiz = null;
  }

  // ---------------------------------------------------------------- 오답 근거 문제 (DEV-I-010, 디자인 상태표 EV장)

  private openEvidence(assignmentId: string | null, attemptId: string): void {
    this.closeOverlay();
    this.dropResult();
    this.assignmentId = assignmentId;
    this.evidence = blankEvidence(assignmentId, attemptId);
    this.owner = this.me?.id ?? null;
    this.renderEvidenceScreen();
    void this.loadEvidence('open');
  }

  private renderEvidenceScreen(): void {
    const S = this.evidence;
    if (S === null) return;
    const focus = document.activeElement instanceof HTMLElement ? document.activeElement.getAttribute('data-testid') : null;
    clear($app());
    $app().append(renderEvidence(S, this.evTab, {
      refresh: () => void this.loadEvidence('refresh'),
      toResult: () => this.openResult(S.assignmentId, S.attemptId),
      toList: () => this.navigate(null),
      select: (aid, optionId) => { this.evTab.drafts.set(aid, optionId); this.evTab.notes.delete(aid); this.renderEvidenceScreen(); },
      submit: (aid) => {
        const opt = this.evTab.drafts.get(aid);
        if (opt === undefined) return;
        this.evTab.notes.delete(aid);
        this.submitter.start(aid, S.attemptId, opt);
      },
      recheck: (aid) => {
        const p = this.submitter.get(aid);
        if (p?.phase === 'key_conflict') {
          // 키 충돌: 조회만. 누른 뒤 시작한 조회가 성공해야 확인 시각을 보인다
          this.evTab.conflictAsked.set(aid, Date.now());
          void this.loadEvidence('conflict');
          return;
        }
        this.submitter.recheck(aid);
      },
      pending: (aid) => { const p = this.submitter.get(aid); return p !== undefined && p.attemptId === S.attemptId ? p : undefined; },
    }, this.evLastQ));
    if (focus !== null) document.querySelector<HTMLElement>(`[data-testid="${focus}"]`)?.focus();
  }

  /**
   * 근거 문제(+ 처음이면 문항) 조회. 결과 화면과 같은 기다리기(30초 확인 제한, 401·403·404 조기 처리, 끝난 묶음의 남은 GET 취소·늦은 응답 버림).
   * 동시에 하나. 진행 중이면 끝난 뒤 한 번 더(제출 확정 직후, 확정 전에 시작된 조회로 끝내지 않게). 자동 반복 없음.
   * why = 'check'(미확정 제출 확인용)이거나 미확정 기록이 있는 동안의 실패·시간 초과는 화면을 바꾸지 않는다(미확정 카드·다시 확인 유지).
   */
  private async loadEvidence(why: 'open' | 'refresh' | 'check' | 'conflict' | 'again' | 'relogin' | 'after-submit' | 'after-reject'): Promise<void> {
    const S = this.evidence;
    if (S === null) return;
    if (S.busy) { if (why !== 'check') S.again = true; return; }
    const startedAt = Date.now();
    const seq = ++this.resultSeq;
    S.seq = seq; S.busy = true;
    this.renderEvidenceScreen();
    const c = this.ctx();
    const att = S.attemptId;
    this.evAbort?.abort();
    const ac = new AbortController();
    this.evAbort = ac;
    const get = (path: string) => this.t.request('GET', path, { signal: ac.signal });
    const outcome = await awaitResultBatch({
      grading: get(`/attempts/${att}/evidence`),
      items: S.items === null ? get(`/attempts/${att}/items`) : null,
      assignment: null,
    }, { timeoutMs: this.resultCheckMs, onSettle: (o) => { if (o.kind !== 'complete') ac.abort(); if (this.evAbort === ac) this.evAbort = null; } });
    if (!this.live(c) || this.evidence !== S || S.seq !== seq || S.attemptId !== att) {
      devlog('evidence', 'stale_response_dropped', null, `att=${att} seq=${seq} why=${why}`);
      return;
    }
    S.busy = false;
    const d = decideEvidenceBatch(att, outcome);
    const pendingHere = this.submitter.all().some((p) => p.attemptId === att);
    switch (d.kind) {
      case 'relogin': S.ev = null; S.phase = 'loading'; this.renderEvidenceScreen(); this.openRelogin(); return;
      case 'denied': case 'notfound': case 'account':
        S.ev = null; S.phase = d.kind; this.renderEvidenceScreen();
        if (d.kind === 'account') await this.refreshStatusScreen();
        return;
      case 'notsubmitted': S.ev = null; S.phase = 'notsubmitted'; this.renderEvidenceScreen(); return;
      case 'error':
        if (d.cause === 'attempt_mismatch') devlog('evidence', 'attempt_mismatch', null, `asked=${att}`);
        // 키 충돌 확인 조회 실패: 새 확인 시각·정상 확인 문구를 보이지 않는다
        for (const [aid, at] of this.evTab.conflictAsked) if (at <= startedAt) this.evTab.conflictAsked.delete(aid);
        if (why === 'check' || why === 'conflict' || (pendingHere && S.ev !== null)) {
          // 제출 확인용 조회 실패: 미확정 기록·카드·'다시 확인'을 지우지 않는다
          devlog('evidence', 'check_failed', null, d.cause);
          if (why !== 'check') toast('확인하지 못했어요. 다시 확인해 주세요.');
          this.renderEvidenceScreen();
        } else {
          S.ev = null; S.phase = 'error'; S.errorCause = d.cause; this.renderEvidenceScreen();
        }
        break;
      case 'ok': {
        if (d.items.kind === 'fresh') S.items = d.items.data;
        else if (d.items.kind === 'failed') devlog('evidence', 'items_failed', null, d.items.cause);
        S.ev = d.ev; S.phase = 'ok'; S.errorCause = null; S.checkedAt = Date.now();
        // 거부 안내는 거부 직후 조회 한 번에만
        for (const [k, n] of this.evTab.notes) { if (n.fresh) n.fresh = false; else this.evTab.notes.delete(k); }
        const now = Date.now();
        for (const a of d.ev.assignments) {
          const aid = a.assignmentId;
          // question_not_provided 거부 뒤 재조회 성공: open이면 그때만 '다시 불러왔어요'(이번 조회 한 번). answered면 서버 답 우선
          if (this.evTab.reload.has(aid)) {
            this.evTab.reload.delete(aid);
            if (a.status === 'open') this.evTab.notes.set(aid, { kind: 'reprovide', fresh: false });
            else if (a.status !== 'answered') this.evTab.notes.set(aid, { kind: 'rejected', fresh: false });
          }
          // 키 충돌: 보류·사용 중단·취소면 그 상태를 따른다. open이면 잠금 유지하고 '다시 확인' 뒤 시작한 조회일 때만 확인 시각
          const cp = this.submitter.get(aid);
          if (cp?.phase === 'key_conflict' && cp.attemptId === att) {
            if (a.status === 'on_hold' || a.status === 'question_unavailable' || a.status === 'cancelled') {
              this.submitter.discardConflict(aid); this.evTab.conflictAsked.delete(aid); this.evTab.conflictSeen.delete(aid);
            } else if (a.status === 'open') {
              const asked = this.evTab.conflictAsked.get(aid);
              if (asked !== undefined && asked <= startedAt) { this.evTab.conflictSeen.set(aid, now); this.evTab.conflictAsked.delete(aid); }
            }
          }
          if (a.status === 'answered') { this.evTab.conflictAsked.delete(aid); this.evTab.conflictSeen.delete(aid); }
        }
        for (const a of d.ev.assignments) {
          if (a.status === 'answered') {
            // 서버에 제출이 있음(어느 기기든): 미확정 기록을 확정으로 정리. 이 탭에서 고른 답이 저장됐다고 단정하지 않는다(서버 값을 그림)
            const p = this.submitter.confirmByQuery(a.assignmentId);
            if (p !== null && !this.evTab.confirmed.has(a.assignmentId)) this.evTab.confirmed.set(a.assignmentId, { attemptId: att, already: false });
            this.evTab.awaiting.delete(a.assignmentId); this.evTab.drafts.delete(a.assignmentId);
          } else if (a.status !== 'open') {
            this.evTab.drafts.delete(a.assignmentId); // 풀 수 없는 상태: 고른 보기로 제출하지 않음(미확정 기록은 그대로)
          }
        }
        this.renderEvidenceScreen();
        break;
      }
    }
    if (S.again && this.evidence === S) { S.again = false; void this.loadEvidence('again'); return; }
    // 화면을 떠났다 돌아왔을 때: 끝나지 않은 제출을 같은 키·같은 보기로 다시 확인(예약된 재전송이 없을 때만)
    if (why === 'open' && this.evidence === S) {
      for (const p of this.submitter.all()) if (p.attemptId === att && (p.phase === 'sending' || p.phase === 'uncertain') && this.submitter.idle(p.assignmentId)) void this.submitter.drive(p.assignmentId);
    }
  }

  /**
   * 결과(+ 처음이면 문항·과제) 조회. 동시에 하나(조회 중에는 확인 단추를 막음), 자동 반복 없음.
   * 응답 반영 조건: 세대·계정 같음, 결과 화면이 그대로(같은 화면 값 객체), 요청 번호가 마지막 번호, 화면의 시도 = 요청한 시도,
   * 응답 본문 attemptId = 요청한 시도(result-model decideBatch).
   * (DEV-I-008) 묶음은 요청 완료와 독립된 확인 제한(초기값 30초) 안에서만 기다린다. 401·403·404가 오면 나머지를 기다리지 않는다.
   * 끝난 묶음의 남은 GET은 취소하고, 그 뒤 도착한 응답은 버린다.
   */
  private async loadResult(): Promise<void> {
    const S = this.result;
    if (S === null || S.busy) return;
    const seq = ++this.resultSeq;
    S.seq = seq; S.busy = true;
    this.renderResultScreen();
    const c = this.ctx();
    const att = S.attemptId;
    this.resultAbort?.abort();
    const ac = new AbortController();
    this.resultAbort = ac;
    const get = (path: string) => this.t.request('GET', path, { signal: ac.signal });
    const outcome = await awaitResultBatch({
      grading: get(`/attempts/${att}/grading`),
      items: S.items === null ? get(`/attempts/${att}/items`) : null,
      assignment: S.assignment === null && S.assignmentId !== null ? get(`/assignments/${S.assignmentId}`) : null,
    }, {
      timeoutMs: this.resultCheckMs,
      // 다 끝난 묶음은 취소할 것이 없다. 시간 초과·조기 종료면 남은 요청을 취소(누적 방지)
      onSettle: (o) => { if (o.kind !== 'complete') ac.abort(); if (this.resultAbort === ac) this.resultAbort = null; },
    });
    if (!this.live(c) || this.result !== S || S.seq !== seq || S.attemptId !== att) {
      devlog('result', 'stale_response_dropped', null, `att=${att} seq=${seq}`);
      return;
    }
    S.busy = false;
    if (outcome.kind === 'timeout') devlog('result', 'result_check_timeout', null, outcome.pending.join(','));
    if (outcome.kind === 'early') devlog('result', 'early_stop', null, `${outcome.slot}=${outcome.result.status}`);
    const d = outcome.kind === 'complete' ? decideBatch(att, outcome.grading, outcome.items, outcome.assignment)
      : outcome.kind === 'early' ? decideEarly(outcome.result)
        : { kind: 'error' as const, cause: 'timeout' as const };
    const clearShown = () => { S.grading = null; S.items = null; S.changed = new Set(); S.itemsFailed = false; };
    switch (d.kind) {
      case 'relogin':
        // 인증 오류가 우선: 다른 요청이 성공했어도 결과를 계속 보이지 않는다
        clearShown(); S.phase = 'loading'; this.renderResultScreen(); this.openRelogin(); return;
      case 'denied': clearShown(); S.baseline = null; S.phase = 'denied'; this.renderResultScreen(); return;
      case 'notfound': clearShown(); S.baseline = null; S.phase = 'notfound'; this.renderResultScreen(); return;
      case 'account': clearShown(); S.baseline = null; S.phase = 'account'; this.renderResultScreen(); await this.refreshStatusScreen(); return;
      case 'notsubmitted': clearShown(); S.phase = 'notsubmitted'; this.renderResultScreen(); return;
      case 'error':
        // 다시 확인 실패 포함: 지난 정오·집계를 숨기고 오류와 다시 확인 단추(통신 실패 ≠ 결과 대기)
        if (d.cause === 'attempt_mismatch') devlog('result', 'attempt_mismatch', null, `asked=${att}`);
        S.grading = null; S.changed = new Set(); S.phase = 'error'; S.errorCause = d.cause; this.renderResultScreen(); return;
      case 'ok': {
        if (d.items.kind === 'fresh') { S.items = d.items.data; S.itemsFailed = false; }
        else if (d.items.kind === 'failed') { S.itemsFailed = true; devlog('result', 'items_failed', null, d.items.cause); }
        if (d.assignment !== null) S.assignment = d.assignment;
        S.changed = changedPoints(S.baseline, d.grading);
        S.grading = d.grading; S.baseline = d.grading;
        S.phase = 'ok'; S.errorCause = null; S.everLoaded = true; S.checkedAt = Date.now();
        if (S.items !== null && !joinPoints(S.items, d.grading).matched) devlog('result', 'point_mismatch', null, att);
        this.renderResultScreen();
        return;
      }
    }
  }

  // ---------------------------------------------------------------- 내 단어장 (DEV-I-013, 디자인 단어장 v0.1)

  /** 목록 열기. 화면을 열 때마다 새 화면 값. focusWordId: 상세에서 돌아온 경우 초점을 돌릴 행. 수집은 실행하지 않는다(조회만) */
  private openWords(focusWordId: string | null): void {
    this.closeOverlay();
    this.dropResult();
    this.assignmentId = null;
    if (location.pathname !== '/') history.pushState(null, '', '/');
    this.words = blankWordList(focusWordId);
    this.owner = this.me?.id ?? null;
    this.renderWords();
    if (focusWordId === null) document.querySelector<HTMLElement>('[data-testid=words-title]')?.focus();
    void this.loadWords();
  }

  /** 상세 열기(별도 화면). 목록이 확인 중이면 이전 행으로 들어가지 않는다 */
  private openWordDetail(wordId: string): void {
    const L = this.words;
    if (L === null || L.view !== 'list' || L.busy || L.phase !== 'ok') return;
    this.closeOverlay();
    this.dropResult();
    this.words = blankWordDetail(wordId);
    this.owner = this.me?.id ?? null;
    this.renderWords();
    void this.loadWords();
  }

  private renderWords(): void {
    const S = this.words;
    if (S === null) return;
    const focus = document.activeElement instanceof HTMLElement && document.activeElement.closest('#app') !== null ? document.activeElement.getAttribute('data-testid') : null;
    const H = {
      refresh: () => void this.loadWords(),
      toList: () => this.navigate(null),
      openWord: (id: string) => this.openWordDetail(id),
      backToWords: () => this.openWords(S.view === 'detail' ? S.wordId : null),
      openQuiz: () => this.openQuizList(),
    };
    clear($app());
    $app().append(this.topbar(), h('div', { class: 'body' }, S.view === 'list' ? renderWordList(S, H) : renderWordDetail(S, H)));
    if (focus !== null) document.querySelector<HTMLElement>(`#app [data-testid="${focus}"]`)?.focus();
  }

  /** 지금 화면의 요청 문맥(응답 반영 확인용) */
  private wordsNow(): WordsReqCtx | null {
    const S = this.words;
    return S === null ? null : { gen: currentGen(), me: this.me?.id ?? null, screen: S, seq: S.seq, wordId: S.view === 'detail' ? S.wordId : null };
  }

  /**
   * 목록 또는 상세 조회 하나. 열 때와 학생의 '다시 확인'에만(자동 반복 없음). 동시에 하나(확인 중에는 다시 확인·이전 행 상세 진입을 막음).
   * 확인 제한(초기값 30초)은 목록·상세 각 요청마다 따로. 실패·시간 초과면 지난 단어·뜻·출처를 숨긴다.
   * 응답 반영 조건: 로그인 세대·계정·화면 값·요청 번호·wordId가 보낼 때와 같음(+ 상세 응답의 wordId = 요청한 wordId). 취소 여부와 별개.
   */
  private async loadWords(): Promise<void> {
    const S = this.words;
    if (S === null || S.busy) return;
    const seq = ++this.wordsSeq;
    S.seq = seq; S.busy = true;
    // 목록을 한 번 받았거나 오류를 보인 뒤의 조회 = 재확인(U-1). 처음 열 때는 제목 초점, 복귀는 행 초점 규칙이 우선
    if (S.view === 'list') S.recheck = S.everLoaded || S.phase === 'error';
    this.renderWords();
    const sent: WordsReqCtx = { gen: currentGen(), me: this.me?.id ?? null, screen: S, seq, wordId: S.view === 'detail' ? S.wordId : null };
    this.wordsAbort?.abort();
    const ac = new AbortController();
    this.wordsAbort = ac;
    const path = S.view === 'detail' ? `/me/words/${encodeURIComponent(S.wordId)}` : '/me/words';
    const outcome = await awaitResultBatch({ grading: this.t.request('GET', path, { signal: ac.signal }), items: null, assignment: null }, {
      timeoutMs: this.wordsCheckMs,
      onSettle: (o) => { if (o.kind !== 'complete') ac.abort(); if (this.wordsAbort === ac) this.wordsAbort = null; },
    });
    if (!wordsResponseUsable(sent, this.wordsNow())) {
      devlog('words', 'stale_response_dropped', null, `view=${S.view} seq=${seq}${sent.wordId !== null ? ` word=${sent.wordId}` : ''}`);
      return;
    }
    S.busy = false;
    const d = decideWords(sent.wordId, outcome);
    const hide = () => { if (S.view === 'list') S.words = null; else S.word = null; };
    switch (d.kind) {
      case 'relogin': hide(); S.phase = 'loading'; this.renderWords(); this.openRelogin(); return;
      case 'denied': hide(); S.phase = 'denied'; break;
      case 'notfound': hide(); S.phase = 'notfound'; break;
      case 'error':
        devlog('words', `check_failed_${d.cause}`, null, `view=${S.view}`);
        hide(); S.phase = 'error'; S.errorCause = d.cause; break;
      case 'ok':
        if (S.view === 'list') S.words = d.data as WordItem[]; else S.word = d.data as WordItem;
        S.phase = 'ok'; S.errorCause = null; S.everLoaded = true; S.checkedAt = Date.now();
        break;
    }
    this.renderWords();
    this.focusAfterWords(S);
  }

  /** 상세에서 돌아온 첫 조회 뒤: 그 행으로 초점. 행이 없으면 빈 목록 안내 또는 목록 제목. 오류·404 등은 그 안내 제목으로 */
  private focusAfterWords(S: WordsState): void {
    const q = (sel: string) => document.querySelector<HTMLElement>(`#app ${sel}`);
    if (S.phase !== 'ok') { if (S.view === 'detail' || S.phase !== 'loading') q('[data-testid=words-state-title]')?.focus(); return; }
    if (S.view === 'detail') { if (document.activeElement === document.body || document.activeElement === null || !document.activeElement.closest('#app')) q('[data-testid=wd-headword]')?.focus(); return; }
    if (S.focusWordId === null) {
      // (DEV-I-014 U-1) 재확인 때문에 초점이 화면 밖(body 등)으로 빠졌으면 다시 활성화된 '다시 확인'으로. 학생이 옮긴 초점은 그대로
      const ae = document.activeElement;
      if (S.recheck && (ae === null || ae === document.body || !ae.closest('#app'))) q('[data-testid=words-refresh]')?.focus();
      S.recheck = false;
      return;
    }
    S.recheck = false;
    const id = S.focusWordId;
    S.focusWordId = null;
    (q(`[data-testid="wrow-${id}"]`) ?? q('[data-testid=words-empty]') ?? q('[data-testid=words-title]'))?.focus();
  }

  // ---------------------------------------------------------------- 단어 시험 (DEV-I-020)

  private openQuiz(view: 'list' | 'play' | 'result', quizId: string | null): QuizState {
    this.closeOverlay();
    this.dropResult();
    this.assignmentId = null;
    if (location.pathname !== '/') history.pushState(null, '', '/');
    const S = blankQuiz(view, quizId);
    // 시작 요청의 응답을 받지 못한 채 화면을 떠났다면, 새 화면에서도 같은 요청으로만 이어서 확인하게 한다
    if (view === 'list' && this.quizTab.startKey !== null) S.startNote = 'start_uncertain';
    this.quiz = S;
    this.owner = this.me?.id ?? null;
    this.renderQuiz();
    document.querySelector<HTMLElement>('#app [data-testid=quiz-title]')?.focus();
    void this.loadQuiz();
    return S;
  }
  private openQuizList(): void { this.openQuiz('list', null); }
  private openQuizPlay(quizId: string): void { this.openQuiz('play', quizId); }
  private openQuizResult(quizId: string): void { this.openQuiz('result', quizId); }

  private quizHandlers(S: QuizState): QuizHandlers {
    return {
      refresh: () => void this.loadQuiz(),
      toWords: () => this.openWords(null),
      toList: () => this.openQuizList(),
      start: () => void this.startQuiz(),
      open: (row: QuizListRow) => { if (row.status === 'in_progress') this.openQuizPlay(row.quizId); else this.openQuizResult(row.quizId); },
      pick: (optionId: string) => {
        const it = S.quiz === null ? null : nextUnanswered(S.quiz);
        if (it === null || S.submitting || this.pendingFor(S) !== null) return;
        S.draftItemId = it.itemId; S.draftOption = optionId; S.submitNote = null;
        this.renderQuiz();
      },
      submit: () => void this.submitAnswer(S),
      resend: () => void this.sendPending(S),
      toResult: () => { if (S.quizId !== null) this.openQuizResult(S.quizId); },
      toPlay: () => { if (S.quizId !== null) this.openQuizPlay(S.quizId); },
      pending: () => this.pendingFor(S),
      startPending: () => this.quizTab.startKey !== null,
    };
  }

  /** 이 화면 시험의 미확정 제출 기록(다른 시험·다른 계정 것은 보이지 않는다) */
  private pendingFor(S: QuizState): PendingAnswer | null {
    const p = this.quizTab.pending;
    return p !== null && p.owner === (this.me?.id ?? null) && p.quizId === S.quizId ? p : null;
  }

  private syncResponseClock(): void {
    const S = this.quiz;
    const item = S?.view === 'play' && S.phase === 'ok' && !S.busy && !S.submitting && S.quiz !== null && S.quiz.status === 'in_progress' && this.pendingFor(S) === null ? nextUnanswered(S.quiz) : null;
    if (!document.hidden && S?.quiz !== null && S?.quiz !== undefined && item !== null) this.responseClock.show(`${S.quiz.quizId}/${item.itemId}`);
    else this.responseClock.pause();
  }

  private renderQuiz(): void {
    const S = this.quiz;
    if (S === null) return;
    const focus = document.activeElement instanceof HTMLElement && document.activeElement.closest('#app') !== null ? document.activeElement.getAttribute('data-testid') : null;
    const H = this.quizHandlers(S);
    this.responseClock.pause();
    clear($app());
    $app().append(this.topbar(), h('div', { class: 'body' }, S.view === 'list' ? renderQuizList(S, H) : S.view === 'play' ? renderQuizPlay(S, H) : renderQuizResult(S, H)));
    requestAnimationFrame(() => { if (this.quiz === S) this.syncResponseClock(); });
    if (focus !== null) document.querySelector<HTMLElement>(`#app [data-testid="${focus}"]`)?.focus();
  }

  private focusQuiz(sel: string): void { document.querySelector<HTMLElement>(`#app ${sel}`)?.focus(); }

  private quizNow(): QuizReqCtx | null {
    const S = this.quiz;
    return S === null ? null : { gen: currentGen(), me: this.me?.id ?? null, screen: S, seq: S.seq, quizId: S.quizId };
  }

  /** 지금 화면 값(목록·시험·결과) 조회 하나. 열 때와 '다시 확인'에만. 취소는 GET만. 실패·시간 초과면 지난 시험·정오·뜻을 숨긴다 */
  private async loadQuiz(): Promise<void> {
    const S = this.quiz;
    if (S === null || S.busy) return;
    const seq = ++this.quizSeq;
    S.seq = seq; S.busy = true;
    this.renderQuiz();
    const sent: QuizReqCtx = { gen: currentGen(), me: this.me?.id ?? null, screen: S, seq, quizId: S.quizId };
    this.quizAbort?.abort();
    const ac = new AbortController();
    this.quizAbort = ac;
    const path = S.view === 'list' ? '/me/word-quizzes' : S.view === 'play' ? `/me/word-quizzes/${encodeURIComponent(S.quizId!)}` : `/me/word-quizzes/${encodeURIComponent(S.quizId!)}/result`;
    const outcome = await awaitResultBatch({ grading: this.t.request('GET', path, { signal: ac.signal }), items: null, assignment: null }, {
      timeoutMs: this.quizCheckMs,
      onSettle: (o) => { if (o.kind !== 'complete') ac.abort(); if (this.quizAbort === ac) this.quizAbort = null; },
    });
    if (!quizResponseUsable(sent, this.quizNow())) { devlog('quiz', 'stale_response_dropped', null, `view=${S.view} seq=${seq}`); return; }
    S.busy = false;
    const hide = () => { S.rows = null; S.quiz = null; S.result = null; S.notFinished = false; };
    const d = S.view === 'list' ? decideQuizList(outcome) : S.view === 'play' ? decideQuizGet(S.quizId!, outcome) : decideQuizResult(S.quizId!, outcome);
    switch (d.kind) {
      case 'relogin': hide(); S.phase = 'loading'; this.renderQuiz(); this.openRelogin(); return;
      case 'denied': hide(); S.phase = 'denied'; break;
      case 'notfound': hide(); S.phase = 'notfound'; break;
      case 'not_finished': hide(); S.notFinished = true; S.phase = 'ok'; S.everLoaded = true; S.checkedAt = Date.now(); break;
      case 'error': devlog('quiz', `check_failed_${d.cause}`, null, `view=${S.view}`); hide(); S.phase = 'error'; S.errorCause = d.cause; break;
      case 'ok':
        hide();
        if (S.view === 'list') S.rows = d.data as QuizListRow[];
        else if (S.view === 'play') {
          const q = d.data as import('../core/quiz-model.js').Quiz;
          S.quiz = q;
          // 서버에 이미 낸 응답이 보이면 미확정 기록은 끝난 것이다(같은 문항을 다시 보내지 않는다)
          const p = this.pendingFor(S);
          if (p !== null && (q.items.find((i) => i.itemId === p.itemId)?.answered ?? false)) this.quizTab.pending = null;
        } else S.result = d.data as import('../core/quiz-model.js').QuizResult;
        S.phase = 'ok'; S.errorCause = null; S.everLoaded = true; S.checkedAt = Date.now();
        break;
    }
    this.renderQuiz();
    if (S.phase !== 'ok') { this.focusQuiz('[data-testid=quiz-state-title]'); return; }
    if (S.view === 'play') this.focusQuiz(S.quiz?.status === 'in_progress' ? '[data-testid=quiz-headword]' : '[data-testid=quiz-all-done-title], [data-testid=quiz-stopped-title]');
    else if (S.view === 'result') this.focusQuiz('[data-testid=quiz-score-title], [data-testid=quiz-stopped-title], [data-testid=quiz-not-finished-title]');
  }

  /** 새 시험 시작. 확정 전에는 같은 키로만 다시 보낸다(키 하나 = 시작 의도 하나). 취소하지 않는다 */
  private async startQuiz(): Promise<void> {
    let S = this.quiz;
    if (S === null) return;
    if (S.view !== 'list') { this.openQuizList(); S = this.quiz; if (S === null) return; }
    if (S.starting) return;
    const key = (this.quizTab.startKey ??= newRequestKey());
    const owner = this.me?.id ?? null;
    const gen = currentGen();
    S.starting = true; S.startNote = null;
    this.renderQuiz();
    const r = await this.t.request('POST', '/me/word-quizzes', { body: {}, key });
    // 계정·세대가 바뀌었으면(키는 이미 지워짐) 이 응답은 어디에도 쓰지 않는다
    if (gen !== currentGen() || owner !== (this.me?.id ?? null) || this.quizTab.owner !== owner) return;
    const d = decideStart(r);
    if (d.kind !== 'uncertain' && d.kind !== 'relogin') this.quizTab.startKey = null;
    if (this.quiz !== S) return; // 화면을 떠남: 확정된 시작은 목록·진행 중 시험 409로 다시 만난다
    S.starting = false;
    switch (d.kind) {
      case 'created': {
        const P = blankQuiz('play', d.quiz.quizId);
        P.quiz = d.quiz; P.phase = 'ok'; P.everLoaded = true; P.checkedAt = Date.now();
        this.quiz = P; this.owner = this.me?.id ?? null;
        this.renderQuiz(); this.focusQuiz('[data-testid=quiz-headword]');
        return;
      }
      case 'in_progress': this.openQuizPlay(d.quizId); return;
      case 'none': S.startNote = d.reason; break;
      case 'uncertain': S.startNote = 'start_uncertain'; break;
      case 'relogin': S.startNote = 'start_uncertain'; this.renderQuiz(); this.openRelogin(); return;
      case 'denied': S.startNote = 'start_denied'; break;
      case 'key_conflict': S.startNote = 'start_conflict'; break;
      case 'error': S.startNote = 'start_error'; break;
    }
    this.renderQuiz();
    this.focusQuiz('[data-testid=quiz-start-note-title]');
  }

  /** 보기를 고른 응답을 제출한다. 보기를 고르지 않았으면 보내지 않는다. 미확정 기록이 있으면 새 키를 만들지 않는다 */
  private async submitAnswer(S: QuizState): Promise<void> {
    if (this.quiz !== S || S.quiz === null || S.submitting) return;
    if (this.pendingFor(S) !== null) { void this.sendPending(S); return; }
    const it = nextUnanswered(S.quiz);
    const me = this.me?.id ?? null;
    if (it === null || me === null || S.draftItemId !== it.itemId || S.draftOption === null) return;
    this.quizTab.pending = { owner: me, quizId: S.quiz.quizId, itemId: it.itemId, optionId: S.draftOption, responseTimeMs: this.responseClock.finish(`${S.quiz.quizId}/${it.itemId}`), key: newRequestKey() };
    await this.sendPending(S);
  }

  private async sendPending(S: QuizState): Promise<void> {
    const p = this.quizTab.pending;
    if (p === null || this.quiz !== S || S.submitting || p.quizId !== S.quizId) return;
    const gen = currentGen();
    S.submitting = true; S.submitNote = null;
    this.renderQuiz();
    // 제출(POST)은 취소하지 않는다. 같은 키·같은 내용
    const r = await this.t.request('POST', `/me/word-quizzes/${encodeURIComponent(p.quizId)}/items/${encodeURIComponent(p.itemId)}/answer`, { body: answerBody(p), key: p.key });
    if (gen !== currentGen() || this.quizTab.pending !== p) return; // 계정 전환·로그아웃으로 기록이 지워짐
    const d = decideAnswer(r);
    if (d.kind !== 'uncertain' && d.kind !== 'relogin') this.quizTab.pending = null;
    if (this.quiz !== S) return;
    S.submitting = false;
    switch (d.kind) {
      case 'accepted':
        S.draftItemId = null; S.draftOption = null; 
        if (S.quiz !== null) S.quiz = applyAccepted(S.quiz, p, d.ack);
        if (d.ack.status === 'completed' || d.ack.resultAvailable) { this.openQuizResult(p.quizId); return; } // 모든 문항을 마침 → 결과(정오는 결과 조회에서만)
        this.renderQuiz(); this.focusQuiz('[data-testid=quiz-headword]');
        return;
      case 'uncertain': this.renderQuiz(); this.focusQuiz('[data-testid=quiz-uncertain-title]'); return;
      case 'relogin': this.renderQuiz(); this.openRelogin(); return;
      case 'denied': S.quiz = null; S.phase = 'denied'; break;
      case 'notfound': S.quiz = null; S.phase = 'notfound'; break;
      case 'stopped': S.submitNote = 'stopped'; break;
      case 'already_answered': S.submitNote = 'already_answered'; S.draftOption = null; break;
      case 'key_conflict': S.submitNote = 'key_conflict'; S.draftOption = null; break;
      case 'rejected': S.submitNote = 'rejected'; S.draftOption = null; break;
    }
    this.renderQuiz();
    // 서버의 지금 상태로 맞춘다(낸 응답이 있는지, 중단됐는지)
    if (d.kind === 'already_answered' || d.kind === 'key_conflict' || d.kind === 'rejected') { const note = S.submitNote; await this.loadQuiz(); if (this.quiz === S && S.phase === 'ok') { S.submitNote = note; this.renderQuiz(); } }
  }

  // ---------------------------------------------------------------- 나가기 (L장)

  private onExit(target: string | null | undefined = undefined): void {
    const to = target === undefined ? this.assignmentId : target;
    const unsaved = this.sm === null ? 0 : (() => { const c = this.sm!.counts(); return c.dirty + c.saving + c.failed + c.conflict; })();
    const uncertain = this.sc?.uncertain() ?? false;
    if (unsaved === 0 && !uncertain) { this.exitToEntry(to); return; }
    const back = () => {
      this.closeOverlay();
      // L-5: 결과 미확정이면 확인 화면으로 돌아간다(잠금 해제된 화면을 보여 주지 않음)
      if (this.sc?.uncertain()) this.onSubmitEvent({ type: 'phase', phase: this.sc.phase });
    };
    this.openOverlay(sheet('exit',
      h('h2', {}, '나갈까요?'),
      unsaved > 0 ? h('p', { 'data-testid': 'exit-unsaved' }, `저장되지 않은 답 ${unsaved}개는 사라져요.`) : null,
      uncertain ? h('p', { 'data-testid': 'exit-uncertain' }, '제출됐는지 아직 확인하지 못했어요. 나가면 이 화면에서 결과를 확인할 수 없어요.') : null,
      h('p', {}, '이미 저장된 답은 그대로 남아요.'),
      h('div', { class: 'acts' },
        h('button', { class: 'primary', 'data-testid': 'exit-stay', onclick: back }, '계속 풀기'),
        h('button', { class: 'secondary', 'data-testid': 'exit-confirm', onclick: () => this.exitToEntry(to) }, '나가기'))));
  }

  private exitToEntry(asg: string | null): void {
    const me = this.me;
    this.cleanupSession();
    this.me = me;
    this.navigate(asg);
  }

  // ---------------------------------------------------------------- 덮개

  private openOverlay(el: HTMLElement): void {
    clear($ov());
    $ov().append(el);
    const f = el.querySelector<HTMLElement>('input, button:not([disabled])');
    f?.focus();
  }
  private closeOverlay(): void { clear($ov()); }
}

/** 화면 값 표시용(테스트 도우미) */
export type { TargetValue };

/**
 * 가상 키보드 대응(태블릿 디자인 반영): 입력란에 초점이 가거나 보이는 화면 높이가 바뀌면 입력란을 보이는 곳으로 스크롤한다.
 * Android(resizes-content)는 레이아웃 높이가 줄고, iPad Safari는 visualViewport만 준다. 둘 다 같은 처리로 맞춘다.
 * 실제 기기 키보드 동작은 미검증.
 */
function keepInputVisibleOnResize(): void {
  const keep = () => {
    const el = document.activeElement;
    if (el instanceof HTMLInputElement && el.closest('#app') !== null) el.scrollIntoView({ block: 'center' });
  };
  document.addEventListener('focusin', (e) => { if (e.target instanceof HTMLInputElement) setTimeout(keep, 250); });
  window.visualViewport?.addEventListener('resize', () => setTimeout(keep, 50));
  window.addEventListener('resize', () => setTimeout(keep, 50));
}
